/* AtmosGuard site, components */
/* eslint-disable no-unused-vars */

const { useState, useEffect, useRef, useMemo } = React;

/* base-path helper for sub-directory pages */
const BASE = (typeof window !== 'undefined' && window.SITE_BASE) || '';
const L = (p) => (p.startsWith('#') || p.startsWith('http') || p.startsWith('mailto:')) ? p : (BASE + p);


/* ---------- Telemetry ticker (above nav) ---------- */
function Ticker() {
  const items = [
    { tag: "TOP RISK", cls: "red", text: "AG-48211, public S3 bucket, prod telemetry · risk 9.4 / 10" },
    { tag: "EXPOSURE", cls: "amber", text: "3 over-privileged identities reach customer-PII storage in 2 hops" },
    { tag: "RANKED", cls: "teal", text: "1,017 signals evaluated → 12 worth your morning. Today's queue is ready." },
    { tag: "POST", cls: "muted", text: "Engineering · ranking by risk, not severity" },
    { tag: "GRAPH", cls: "blue", text: "Lateral-movement chains, per cloud · graph refreshed 0.9s ago" },
    { tag: "REVIEW", cls: "purple", text: "2 outstanding exceptions need an owner sign-off" },
  ];
  const Stream = ({ aria }) => (
    <span className="tk-stream-inner" aria-hidden={aria ? undefined : "true"}>
      {items.concat(items).map((it, i) => (
        <span key={i} className="tk-item">
          <span className={"pill " + it.cls}>{it.tag}</span>
          <span>{it.text}</span>
          <span className="tk-sep">·</span>
        </span>
      ))}
    </span>
  );
  return (
    <div className="ticker" role="status" aria-label="AtmosGuard telemetry ticker">
      <div className="ticker-inner">
        <span className="tk-rail">
          <span className="dot live" />
          <span className="mono">LIVE</span>
        </span>
        <span className="tk-sep">·</span>
        <span className="tk-stream"><Stream /></span>
        <span className="tk-right">
          <span>BUILD 2026.04.24</span>
        </span>
      </div>
    </div>
  );
}

/* ---------- Brand mark ---------- */
function BrandMark() {
  return (
    <img
      className="brand-mark"
      src={L("logo.png")}
      alt=""
      width="22"
      height="22"
      aria-hidden="true"
    />
  );
}

const PRODUCT_LOGOS = {
  posture: "logos/posture-management.svg",
  threat: "logos/threat-management.svg",
  compliance: "logos/compliance.svg",
  appsec: "logos/app-security.svg",
  vuln: "logos/vulnerability-management.svg",
  aigov: "logos/ai-governance.svg",
};

/* Single source of truth for the product line. Nav, mobile nav, footer and
 * the homepage roadmap all render from this list. Order here is display order. */
const PRODUCTS = [
  { id: "atlas", name: "Atlas", category: "CNAPP",
    href: "products/atlas.html", logo: "posture", pill: null,
    desc: "CNAPP · posture, vulnerabilities, and compliance on one graph.",
    roadmap: "live" },
  { id: "forge", name: "Forge", category: "AppSec",
    href: "products/forge.html", logo: "appsec", pill: { cls: "teal", label: "BETA" },
    desc: "AppSec · code flaws, leaked secrets, and risky dependencies, ranked by what runs.",
    roadmap: "beta" },
  { id: "sentry", name: "Sentry", category: "Threat Intelligence",
    href: "products/sentry.html", logo: "threat", pill: { cls: "amber", label: "SOON" },
    desc: "Threat Intelligence · detections enriched with who is exploiting what, fed into the risk engine.",
    roadmap: "soon" },
  { id: "stratos", name: "Stratos", category: "AI Governance",
    href: "products/stratos.html", logo: "aigov", pill: { cls: "amber", label: "SOON" },
    desc: "AI Governance · model inventory, audit trails, and accountable AI use.",
    roadmap: "soon" },
];
function ProductLogo({ kind, size = 28 }) {
  const src = PRODUCT_LOGOS[kind];
  if (!src) return null;
  return (
    <img
      className="product-logo"
      src={L(src)}
      alt=""
      width={size}
      height={size}
      aria-hidden="true"
    />
  );
}

/* ---------- Nav ---------- */
function Nav({ active }) {
  const [time, setTime] = useState(() => nowLabel());
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  useEffect(() => {
    const id = setInterval(() => setTime(nowLabel()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    const onResize = () => { if (window.innerWidth > 1080) setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [mobileOpen]);
  const isActive = (k) => active === k;
  const toggleSection = (k) => setMobileSection((s) => (s === k ? null : k));
  const closeMobile = () => setMobileOpen(false);
  return (
    <nav className={"nav" + (mobileOpen ? " mobile-open" : "")}>
      <div className="nav-inner">
        <a href={L("index.html")} className="brand">
          <BrandMark />
          <span>AtmosGuard</span>
        </a>
        <div className="nav-links">
          <div className={"nav-drop" + (openMenu === "products" ? " open" : "")}
            onMouseEnter={() => setOpenMenu("products")}
            onMouseLeave={() => setOpenMenu(null)}>
            <button className={"nav-link" + (isActive("products") ? " active" : "")}>
              Products <span className="caret">▾</span>
            </button>
            <div className="dropdown dd-single" role="menu">
              <div className="dd-col">
                <span className="dd-eyebrow">Products</span>
                {PRODUCTS.map((p) => (
                  <a key={p.id} className="dd-item" href={L(p.href)}>
                    <span className="dd-name">
                      {p.name}
                      {p.pill && <span className={"pill " + p.pill.cls}>{p.pill.label}</span>}
                    </span>
                    <span className="dd-desc">{p.desc}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
          <a className={"nav-link" + (isActive("how") ? " active" : "")} href={L("index.html#pipeline")}>How it works</a>
          <a className={"nav-link" + (isActive("integrations") ? " active" : "")} href={L("integrations.html")}>Integrations</a>
          <a className={"nav-link" + (isActive("pricing") ? " active" : "")} href={L("pricing.html")}>Pricing</a>
          <a className={"nav-link" + (isActive("blog") ? " active" : "")} href={L("blog/index.html")}>Blog</a>
          <div className={"nav-drop" + (openMenu === "company" ? " open" : "")}
            onMouseEnter={() => setOpenMenu("company")}
            onMouseLeave={() => setOpenMenu(null)}>
            <button className={"nav-link" + (isActive("company") ? " active" : "")}>
              Company <span className="caret">▾</span>
            </button>
            <div className="dropdown dd-right" role="menu">
              <div className="dd-col">
                <span className="dd-eyebrow">Company</span>
                <a className="dd-item" href={L("company.html#about")}><span className="dd-name">About AtmosGuard</span><span className="dd-desc">Who we are and what we stand for.</span></a>
                <a className="dd-item" href={L("company.html#leadership")}><span className="dd-name">Leadership</span><span className="dd-desc">Meet the team behind AtmosGuard.</span></a>
                <a className="dd-item" href={L("careers.html")}><span className="dd-name">Careers</span><span className="dd-desc">Open roles · Dubai &amp; remote.</span></a>
                <a className="dd-item" href={L("contact.html")}><span className="dd-name">Contact</span><span className="dd-desc">Talk to a founder or engineer.</span></a>
              </div>
              <div className="dd-col dd-col-muted">
                <span className="dd-eyebrow">News &amp; press</span>
                <a className="dd-item" href={L("blog/index.html")}><span className="dd-name">Blog</span><span className="dd-desc">Latest news and insights.</span></a>
              </div>
            </div>
          </div>
        </div>
        <div className="nav-right">
          <div className="nav-status">
            <span className="dot live" />
            <span>ALL SYSTEMS · {time} UTC</span>
          </div>
          <span className="nav-sep mono">/</span>
          <a className="btn ghost nav-signin" href="https://app.atmosguard.io">Sign in</a>
          <a className="btn primary nav-demo" href="https://app.atmosguard.io/register">
            Start your free trial
            <span className="kbd mono">↵</span>
          </a>
          <button
            type="button"
            className={"nav-burger" + (mobileOpen ? " open" : "")}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="nav-mobile-panel"
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>
      <div
        id="nav-mobile-panel"
        className={"nav-mobile" + (mobileOpen ? " open" : "")}
        role="dialog"
        aria-label="Site navigation"
        aria-hidden={!mobileOpen}
      >
        <div className="nav-mobile-inner">
          <div className={"nav-mobile-group" + (mobileSection === "products" ? " open" : "")}>
            <button type="button" className="nav-mobile-toggle" onClick={() => toggleSection("products")} aria-expanded={mobileSection === "products"}>
              Products <span className="caret">▾</span>
            </button>
            <div className="nav-mobile-sub">
              {PRODUCTS.map((p) => (
                <a key={p.id} className="nav-mobile-sublink" href={L(p.href)} onClick={closeMobile}>
                  {p.name} <span className="micro" style={{ color: "var(--text-4)", marginLeft: 6 }}>{p.category.toUpperCase()}</span>
                </a>
              ))}
            </div>
          </div>
          <a className={"nav-mobile-link" + (isActive("how") ? " active" : "")} href={L("index.html#pipeline")} onClick={closeMobile}>How it works</a>
          <a className={"nav-mobile-link" + (isActive("integrations") ? " active" : "")} href={L("integrations.html")} onClick={closeMobile}>Integrations</a>
          <a className={"nav-mobile-link" + (isActive("pricing") ? " active" : "")} href={L("pricing.html")} onClick={closeMobile}>Pricing</a>
          <a className={"nav-mobile-link" + (isActive("blog") ? " active" : "")} href={L("blog/index.html")} onClick={closeMobile}>Blog</a>
          <div className={"nav-mobile-group" + (mobileSection === "company" ? " open" : "")}>
            <button type="button" className="nav-mobile-toggle" onClick={() => toggleSection("company")} aria-expanded={mobileSection === "company"}>
              Company <span className="caret">▾</span>
            </button>
            <div className="nav-mobile-sub">
              <a className="nav-mobile-sublink" href={L("company.html#about")} onClick={closeMobile}>About AtmosGuard</a>
              <a className="nav-mobile-sublink" href={L("company.html#leadership")} onClick={closeMobile}>Leadership</a>
              <a className="nav-mobile-sublink" href={L("careers.html")} onClick={closeMobile}>Careers</a>
              <a className="nav-mobile-sublink" href={L("contact.html")} onClick={closeMobile}>Contact</a>
            </div>
          </div>
          <div className="nav-mobile-cta">
            <a className="btn ghost" href="https://app.atmosguard.io">Sign in</a>
            <a className="btn primary" href="https://app.atmosguard.io/register" onClick={() => setMobileOpen(false)}>
              Start your free trial
            </a>
          </div>
        </div>
      </div>
    </nav>
  );
}

function nowLabel() {
  const d = new Date();
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section id="top" className="hero">
      <div className="wrap-wide hero-grid">
        <div>
          <span className="hero-kicker">
            <span className="dot live" />
            Risk-first · in private beta
          </span>
          <h1>
            1,017 signals.<br />
            <span style={{ color: "var(--teal)" }}>12 worth your morning.</span>
            <span className="mono-sub">risk-first cloud security, we point you at what matters</span>
          </h1>
          <p className="subcopy">
            Most cloud security tools hand you a queue and wish you luck. AtmosGuard ranks
            every exposure by <span style={{ color: "var(--text)" }}>risk</span>,
            who it touches, what it can reach, and how an attacker would chain it, so the
            top of the list is always the thing actually worth fixing today. AWS + Azure,
            read-only, in private beta with a small group of pilot teams.
          </p>
          <div className="hero-cta">
            <a className="btn primary lg" href="https://app.atmosguard.io/register">Start your free trial</a>
            <a className="btn lg" href={L("products/atlas.html")}>See the product</a>
            <span className="trust">AWS + Azure · read-only roles · no agents</span>
          </div>
        </div>
        <HeroConsole />
      </div>
    </section>
  );
}

function HeroConsole() {
  return (
    <div className="console" aria-label="AtmosGuard risk-ranked exposure queue, mock UI">
      <div className="console-bar">
        <span className="dot live" />
        <span className="path">atmosguard</span>
        <span className="sep">/</span>
        <span className="path">today</span>
        <span className="sep">/</span>
        <span className="path">top exposures</span>
        <div className="right">
          <span>graph fresh <span style={{ color: "var(--text-2)" }}>0.9s ago</span></span>
          <span className="kbd">⌘K</span>
        </div>
      </div>
      <div className="console-body">
        <aside className="console-side">
          <div className="side-group"><span className="micro">Today's queue</span></div>
          <div className="side-item active">Top exposures<span className="count">12</span></div>
          <div className="side-item">Worth a glance<span className="count">38</span></div>
          <div className="side-item">Background<span className="count">967</span></div>
          <div className="side-group" style={{ marginTop: 10 }}><span className="micro">By risk</span></div>
          <div className="side-item"><span className="dot" style={{ background: "var(--red)", boxShadow: "0 0 6px color-mix(in oklab,var(--red) 50%,transparent)" }} />Critical<span className="count">3</span></div>
          <div className="side-item"><span className="dot" style={{ background: "var(--amber)", boxShadow: "0 0 6px color-mix(in oklab,var(--amber) 50%,transparent)" }} />Elevated<span className="count">9</span></div>
          <div className="side-item"><span className="dot" style={{ background: "var(--blue)", boxShadow: "0 0 6px color-mix(in oklab,var(--blue) 50%,transparent)" }} />Watch<span className="count">38</span></div>
        </aside>
        <div className="console-main">
          <div className="console-filters">
            <span className="filter-chip live"><span className="dot live" />Ranked</span>
            <span className="filter-chip"><span className="mono">order:</span> risk ↓</span>
            <span className="filter-chip"><span className="mono">cloud:</span> aws, azure</span>
            <span className="filter-chip" style={{ marginLeft: "auto" }}><span className="mono" style={{ color: "var(--text)" }}>12</span>&nbsp;<span style={{ color: "var(--text-3)" }}>of 1,017</span></span>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th className="sel"><span className="cb on" /></th>
                  <th style={{ width: 46 }}>#</th>
                  <th style={{ width: 64 }}>Risk</th>
                  <th>Exposure</th>
                  <th style={{ width: 64 }}>Cloud</th>
                  <th style={{ width: 160 }}>Why it matters</th>
                  <th style={{ width: 64 }}>Hops</th>
                </tr>
              </thead>
              <tbody>
                <RiskRow rank="01" score={9.4} res="s3://prod-telemetry-log-archive" cloud="aws" why="Public · reaches PII export" hops="—" sel />
                <RiskRow rank="02" score={9.1} res="iam/role/svc-etl" cloud="aws" why="Admin* · 4 prod accounts" hops="2" />
                <RiskRow rank="03" score={8.7} res="storage/stg-customer-pii-eus" cloud="azure" why="Public blob · contains PII" hops="—" />
                <RiskRow rank="04" score={7.6} res="vnet/prod-eus-01/subnet-db" cloud="azure" why="0.0.0.0/0 → mysql 3306" hops="1" />
                <RiskRow rank="05" score={6.9} res="keyvault/kv-payments-prd" cloud="azure" why="No diag · key for payments" hops="—" />
                <RiskRow rank="06" score={6.4} res="rds/prd-mysql-04" cloud="aws" why="No CMK · backup is public" hops="2" />
              </tbody>
            </table>
          </div>
          <MiniLive />
        </div>
      </div>
    </div>
  );
}

/* Risk-ranked row, shows risk score as a horizontal bar */
function RiskRow({ rank, score, res, cloud, why, hops, sel }) {
  const tone = score >= 9 ? "red" : score >= 7 ? "amber" : "blue";
  const toneVar = `var(--${tone})`;
  const pct = Math.min(100, Math.round((score / 10) * 100));
  return (
    <tr>
      <td className="sel"><span className={"cb" + (sel ? " on" : "")} /></td>
      <td><span className="mono id" style={{ color: "var(--text-3)" }}>{rank}</span></td>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="mono" style={{ color: toneVar, fontFeatureSettings: '"tnum"', fontWeight: 600, fontSize: 12 }}>{score.toFixed(1)}</span>
        </div>
        <div style={{ height: 3, background: "var(--bg-2)", borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
          <span style={{ display: "block", height: "100%", width: pct + "%", background: `linear-gradient(90deg, ${toneVar}, color-mix(in oklab, ${toneVar} 60%, transparent))` }} />
        </div>
      </td>
      <td><span className="mono" style={{ color: "var(--text-2)" }}>{res}</span></td>
      <td>
        <span className="cloud">
          <span className="dot" style={{ background: cloud === "aws" ? "var(--aws)" : "var(--azure)", boxShadow: `0 0 6px ${cloud === "aws" ? "rgba(255,153,0,0.35)" : "rgba(51,153,255,0.35)"}` }} />
          <span className={"chip " + cloud}>{cloud.toUpperCase()}</span>
        </span>
      </td>
      <td><span style={{ color: "var(--text-2)", fontSize: 12.5 }}>{why}</span></td>
      <td><span className="mono id" style={{ color: hops === "—" ? "var(--text-4)" : "var(--text-2)" }}>{hops}</span></td>
    </tr>
  );
}

function MiniLive() {
  const [vals, setVals] = useState(() => seed(42));
  useEffect(() => {
    const id = setInterval(() => {
      setVals(v => {
        const n = v.slice(1);
        const last = v[v.length - 1];
        const drift = (Math.random() - 0.5) * 6;
        n.push(Math.max(4, Math.min(38, last + drift)));
        return n;
      });
    }, 900);
    return () => clearInterval(id);
  }, []);
  const w = 236, h = 42, pad = 2;
  const max = Math.max(...vals), min = Math.min(...vals);
  const range = max - min || 1;
  const pts = vals.map((v, i) => {
    const x = pad + (i / (vals.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return [x, y];
  });
  const d = "M " + pts.map(p => p.map(n => n.toFixed(1)).join(",")).join(" L ");
  const area = d + ` L ${(w - pad).toFixed(1)},${(h - pad).toFixed(1)} L ${pad.toFixed(1)},${(h - pad).toFixed(1)} Z`;
  const last = pts[pts.length - 1];
  return (
    <div className="mini-live">
      <div className="mini-live-head">
        <span className="dot live" />
        <span className="micro" style={{ color: "var(--text-3)" }}>EVENTS · 60s</span>
        <span className="spacer" />
        <span className="mono" style={{ color: "var(--text)" }}>{vals[vals.length - 1].toFixed(0)}/s</span>
      </div>
      <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--teal)" stopOpacity="0.28" />
            <stop offset="100%" stopColor="var(--teal)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#sparkFill)" />
        <path d={d} stroke="var(--teal)" strokeWidth="1.25" fill="none" />
        <circle cx={last[0]} cy={last[1]} r="2.4" fill="var(--teal)" />
        <circle cx={last[0]} cy={last[1]} r="6" fill="var(--teal)" opacity="0.18" />
      </svg>
    </div>
  );
}

function seed(n) {
  const out = []; let v = 18;
  for (let i = 0; i < n; i++) { v += (Math.random() - 0.5) * 4; v = Math.max(6, Math.min(32, v)); out.push(v); }
  return out;
}

/* ---------- Stats strip ---------- */
function Stats() {
  return (
    <section className="section">
      <div className="wrap-wide">
        <div className="stats-strip">
          <Stat label="SIGNALS EVALUATED TODAY" val="1,017" delta="→ 12 worth a look" dcls="teal" sub="risk, reach, identity, data class" />
          <Stat label="RESOLUTION RATE" val="92.1%" delta="opened → resolved or excepted" dcls="up" sub="our floor, not our ceiling" />
          <Stat label="DRIFT DETECTION" val="Continuous" delta="config change → graph update" dcls="up" sub="continuous, not batch" />
          <Stat label="NOISE SUPPRESSED" val="−96%" delta="of signals never reach an analyst" dcls="teal" sub="the rest sit in the graph, queryable" />
        </div>
      </div>
    </section>
  );
}
function Stat({ label, val, delta, dcls, sub }) {
  return (
    <div className="stat">
      <span className="bloom dot live" />
      <span className="micro">{label}</span>
      <div className="val mono">{val}</div>
      <div className={"delta " + (dcls || "")}>{delta}</div>
      <div className="delta" style={{ color: "var(--text-4)" }}>{sub}</div>
    </div>
  );
}

/* ---------- Modules ---------- */
function Modules() {
  return (
    <section id="product" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 01 — Product</span></div>
            <h2>Three lenses, one portal.</h2>
            <p style={{ marginTop: 12 }}>
              Posture, Threat, and Compliance share the same risk scoring engine.
              Whatever lens you open, dashboards show you the few risks that would
              actually hurt today and tell you why.
            </p>
          </div>
          <div className="micro" style={{ color: "var(--text-3)" }}>RANKED BY RISK</div>
        </div>

        <div className="modules">
          <Module kind="posture" />
          <Module kind="threat" />
          <Module kind="compliance" />
        </div>
      </div>
    </section>
  );
}

function Module({ kind }) {
  if (kind === "posture") {
    return (
      <div className="module">
        <div className="mod-head">
          <ProductLogo kind="posture" />
          <span className="pill teal">Posture</span>
          <span className="micro" style={{ marginLeft: "auto" }}>CSPM</span>
        </div>
        <h3>The 12 misconfigurations that would hurt today.</h3>
        <p className="mod-desc">
          Every resource scored against your environment, not against a generic catalog.
          The console opens on the few exposures with real risk; the rest stay in
          the graph for when you need them.
        </p>
        <div className="mod-metrics">
          <div className="m"><div className="v">12</div><span className="micro k">TOP RISKS TODAY</span></div>
          <div className="m"><div className="v">38,204</div><span className="micro k">ASSETS IN GRAPH</span></div>
          <div className="m"><div className="v">96%</div><span className="micro k">NOISE SUPPRESSED</span></div>
        </div>
        <div className="mod-vis">
          <PostureRing />
        </div>
      </div>
    );
  }
  if (kind === "threat") {
    return (
      <div className="module">
        <div className="mod-head">
          <ProductLogo kind="threat" />
          <span className="pill red">Threat</span>
          <span className="micro" style={{ marginLeft: "auto" }}>CDR · CIEM</span>
        </div>
        <h3>The chain an attacker would actually take.</h3>
        <p className="mod-desc">
          Lateral-movement paths stitched within each cloud, ranked by what they can
          reach. We surface the chain, not the alert, with the identity, the call, and
          the next step you should take.
        </p>
        <div className="mod-metrics">
          <div className="m"><div className="v">3</div><span className="micro k">CHAINS WORTH ACTING ON</span></div>
          <div className="m"><div className="v">7m 42s</div><span className="micro k">P50 TIME-TO-CLOSE</span></div>
          <div className="m"><div className="v">3.1×</div><span className="micro k">CHAIN-VS-ALERT LIFT</span></div>
        </div>
        <div className="mod-vis">
          <ThreatFeed />
        </div>
      </div>
    );
  }
  return (
    <div className="module">
      <div className="mod-head">
        <ProductLogo kind="compliance" />
        <span className="pill blue">Compliance</span>
        <span className="micro" style={{ marginLeft: "auto" }}>EVIDENCE</span>
      </div>
      <h3>The control your auditor would actually fail you on.</h3>
      <p className="mod-desc">
        Every framework, ranked by what's at risk of slipping, not by alphabetical
        control ID. Evidence is signed and time-stamped, so the controls that look
        green today will still be green at audit time.
      </p>
      <div className="mod-metrics">
        <div className="m"><div className="v">2</div><span className="micro k">CONTROLS AT RISK</span></div>
        <div className="m"><div className="v">94%</div><span className="micro k">EVIDENCE FRESH</span></div>
        <div className="m"><div className="v">−71%</div><span className="micro k">AUDIT PREP TIME</span></div>
      </div>
      <div className="mod-vis">
        <ComplianceGrid />
      </div>
    </div>
  );
}

function PostureRing() {
  // Simple donut split by severity
  const segs = [
    { k: "Healthy", v: 94.2, c: "var(--teal)" },
    { k: "High", v: 1.3, c: "var(--red)" },
    { k: "Medium", v: 2.4, c: "var(--amber)" },
    { k: "Low", v: 2.1, c: "var(--blue)" },
  ];
  const total = 100;
  const r = 36, C = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="ring-wrap">
      <div className="ring">
        <svg width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={r} fill="none" stroke="var(--border)" strokeWidth="8" />
          {segs.map((s, i) => {
            const len = (s.v / total) * C;
            const el = (
              <circle key={i}
                cx="50" cy="50" r={r} fill="none"
                stroke={s.c} strokeWidth="8"
                strokeDasharray={`${len} ${C - len}`}
                strokeDashoffset={-offset}
                transform="rotate(-90 50 50)"
                style={i === 0 ? { filter: "drop-shadow(0 0 6px var(--teal-glow-strong))" } : undefined}
              />
            );
            offset += len;
            return el;
          })}
          <text x="50" y="48" textAnchor="middle" fontFamily="var(--mono)" fontSize="14" fontWeight="600" fill="var(--text)">94.2%</text>
          <text x="50" y="62" textAnchor="middle" fontFamily="var(--mono)" fontSize="8" fill="var(--text-4)" letterSpacing="0.1em">HEALTHY</text>
        </svg>
      </div>
      <div className="ring-legend">
        {segs.map((s, i) => (
          <div key={i} className="r">
            <span className="sw" style={{ background: s.c }} />
            <span style={{ color: "var(--text-2)" }}>{s.k}</span>
            <span style={{ marginLeft: "auto", color: "var(--text-3)" }}>{s.v.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ThreatFeed() {
  const rows = [
    { t: "10:42:08", sev: "HIGH", msg: <>Impossible travel · <span className="hl">iam/svc-deploy</span> · us-east-1 ↔ eu-west-1</>, c: "var(--red)" },
    { t: "10:41:52", sev: "MED", msg: <>Privilege escalation · <span className="hl">role/etl-writer</span></>, c: "var(--amber)" },
    { t: "10:41:30", sev: "HIGH", msg: <>Public blob enabled · <span className="hl">stg-customer-pii</span></>, c: "var(--red)" },
    { t: "10:40:11", sev: "LOW", msg: <>Unused key · <span className="hl">kms/ag-log-cmk</span></>, c: "var(--blue)" },
  ];
  return (
    <div className="feed">
      {rows.map((r, i) => (
        <div key={i} className="feed-row">
          <span className="t">{r.t}</span>
          <span className="sev" style={{ color: r.c }}>{r.sev}</span>
          <span className="msg">{r.msg}</span>
        </div>
      ))}
    </div>
  );
}

function ComplianceGrid() {
  const cells = [
    { name: "SOC 2", pct: "96%", ok: true },
    { name: "ISO 27001", pct: "94%", ok: true },
    { name: "HIPAA", pct: "89%", warn: true },
    { name: "PCI-DSS", pct: "92%", ok: true },
    { name: "CIS", pct: "97%", ok: true },
    { name: "NIST", pct: "88%", warn: true },
  ];
  return (
    <div className="fw-grid">
      {cells.map((c, i) => (
        <div key={i} className={"fw-cell " + (c.ok ? "ok" : c.warn ? "warn" : "")}>
          <span className="name">{c.name}</span>
          <span className="pct">{c.pct}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------- Cloud coverage ---------- */
function Coverage() {
  return (
    <section id="coverage" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 02 — The funnel</span></div>
            <h2>From 38,204 assets to the 12 worth your morning.</h2>
            <p style={{ marginTop: 12 }}>
              Every resource and identity across AWS and Azure flows into the same graph.
              Risk scoring collapses 1,017 raw signals down to a short, ranked
              list, the exposures whose fix actually moves your risk number.
            </p>
          </div>
          <div className="micro" style={{ color: "var(--text-3)" }}>38,204 → 1,017 → 12</div>
        </div>
        <ArchitectureDiagram />
        <div className="coverage" style={{ marginTop: 32 }}>
          <CoverageDiagram />
          <CoverageList />
        </div>
      </div>
    </section>
  );
}

function ArchitectureDiagram() {
  // Sources (clouds + identity) → AtmosGuard control plane → outputs (posture/threat/compliance)
  return (
    <div className="cov-diagram" style={{ marginBottom: 8 }}>
      <svg viewBox="0 0 680 380" width="100%" style={{ display: "block" }}>
        <defs>
          <linearGradient id="archLine" x1="0" x2="1">
            <stop offset="0%" stopColor="var(--teal)" stopOpacity="0.05" />
            <stop offset="50%" stopColor="var(--teal)" stopOpacity="0.7" />
            <stop offset="100%" stopColor="var(--teal)" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="archLine2" x1="0" x2="1">
            <stop offset="0%" stopColor="var(--teal)" stopOpacity="0.7" />
            <stop offset="100%" stopColor="var(--teal)" stopOpacity="0.05" />
          </linearGradient>
          <radialGradient id="archGlow" cx="50%" cy="50%">
            <stop offset="0%" stopColor="var(--teal)" stopOpacity="0.18" />
            <stop offset="100%" stopColor="var(--teal)" stopOpacity="0" />
          </radialGradient>
        </defs>

        <text x="30" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">SOURCES</text>
        <text x="270" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">CONTROL PLANE</text>
        <text x="510" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">OUTPUTS</text>

        {/* Sources */}
        <rect x="20" y="70" width="190" height="68" rx="8" fill="var(--surface)" stroke="var(--border-2)" />
        <text x="36" y="96" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">AWS</text>
        <text x="36" y="116" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">812 accounts · 4 regions</text>
        <circle cx="196" cy="104" r="4" fill="var(--aws)" />
        <rect x="20" y="160" width="190" height="68" rx="8" fill="var(--surface)" stroke="var(--border-2)" />
        <text x="36" y="186" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">Azure</text>
        <text x="36" y="206" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">472 subs · 3 regions</text>
        <circle cx="196" cy="194" r="4" fill="var(--azure)" />
        <rect x="20" y="250" width="190" height="58" rx="8" fill="var(--surface)" stroke="var(--border-2)" />
        <text x="36" y="274" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">Identity</text>
        <text x="36" y="292" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">Entra ID · IAM · SAML 2.0</text>

        {/* Center */}
        <circle cx="340" cy="190" r="110" fill="url(#archGlow)" />
        <rect x="250" y="130" width="180" height="120" rx="12" fill="var(--surface-2)" stroke="var(--teal)" strokeOpacity="0.4" />
        <text x="268" y="158" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">AtmosGuard</text>
        <text x="268" y="176" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.2" fill="var(--text-4)">CONTROL PLANE</text>
        <line x1="268" y1="188" x2="412" y2="188" stroke="var(--border)" />
        <text x="268" y="206" fontFamily="var(--mono)" fontSize="10" fill="var(--text-2)">inventory · graph</text>
        <text x="268" y="222" fontFamily="var(--mono)" fontSize="10" fill="var(--text-2)">risk engine</text>
        <text x="268" y="238" fontFamily="var(--mono)" fontSize="10" fill="var(--text-2)">policy · detections</text>
        <circle cx="420" cy="190" r="3" fill="var(--teal)" />
        <circle cx="420" cy="190" r="10" fill="var(--teal)" opacity="0.18" />

        {/* Outputs */}
        <rect x="490" y="70" width="170" height="58" rx="8" fill="var(--surface)" stroke="var(--border-2)" />
        <text x="506" y="92" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">Posture</text>
        <text x="506" y="110" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">misconfig · drift</text>
        <rect x="490" y="160" width="170" height="58" rx="8" fill="var(--surface)" stroke="var(--border-2)" />
        <text x="506" y="182" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">Threat</text>
        <text x="506" y="200" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">anomaly · lateral move</text>
        <rect x="490" y="250" width="170" height="58" rx="8" fill="var(--surface)" stroke="var(--border-2)" />
        <text x="506" y="272" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">Compliance</text>
        <text x="506" y="290" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">evidence · attestations</text>

        {/* Connectors */}
        <path d="M 210 104 C 230 104, 235 170, 250 170" stroke="url(#archLine)" strokeWidth="1.25" fill="none" />
        <path d="M 210 194 C 230 194, 235 190, 250 190" stroke="url(#archLine)" strokeWidth="1.25" fill="none" />
        <path d="M 210 279 C 230 279, 235 210, 250 210" stroke="url(#archLine)" strokeWidth="1.25" fill="none" />
        <path d="M 430 160 C 460 160, 470 99,  490 99" stroke="url(#archLine2)" strokeWidth="1.25" fill="none" />
        <path d="M 430 190 C 460 190, 470 189, 490 189" stroke="url(#archLine2)" strokeWidth="1.25" fill="none" />
        <path d="M 430 220 C 460 220, 470 279, 490 279" stroke="url(#archLine2)" strokeWidth="1.25" fill="none" />

        <circle cx="222" cy="140" r="1.8" fill="var(--teal)" />
        <circle cx="222" cy="245" r="1.8" fill="var(--teal)" />
        <circle cx="460" cy="130" r="1.8" fill="var(--teal)" />
        <circle cx="460" cy="250" r="1.8" fill="var(--teal)" />

        <text x="30" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">read-only · zero agents</text>
        <text x="270" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">graph refreshed every 90s</text>
        <text x="490" y="352" fontFamily="var(--mono)" fontSize="9" fill="var(--text-3)">Slack · Teams · Email · dashboards</text>
      </svg>
    </div>
  );
}

function CoverageDiagram() {
  // Risk funnel: assets → signals → ranked → the few that matter
  return (
    <div className="cov-diagram">
      <svg viewBox="0 0 680 380" width="100%" style={{ display: "block" }}>
        <defs>
          <linearGradient id="line" x1="0" x2="1">
            <stop offset="0%" stopColor="var(--teal)" stopOpacity="0.05" />
            <stop offset="50%" stopColor="var(--teal)" stopOpacity="0.7" />
            <stop offset="100%" stopColor="var(--teal)" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="line2" x1="0" x2="1">
            <stop offset="0%" stopColor="var(--teal)" stopOpacity="0.7" />
            <stop offset="100%" stopColor="var(--teal)" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="funnelFill" x1="0" x2="1">
            <stop offset="0%" stopColor="var(--teal)" stopOpacity="0.05" />
            <stop offset="100%" stopColor="var(--teal)" stopOpacity="0.18" />
          </linearGradient>
        </defs>

        {/* eyebrow markers */}
        <text x="30" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">EVERYTHING</text>
        <text x="230" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">RAW SIGNALS</text>
        <text x="410" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">RANKED</text>
        <text x="572" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">WORTH FIXING</text>

        {/* Funnel silhouette */}
        <path d="M 30 60 L 220 60 L 220 320 L 30 320 Z" fill="url(#funnelFill)" opacity="0.4" />
        <path d="M 220 60 L 400 110 L 400 270 L 220 320 Z" fill="url(#funnelFill)" opacity="0.6" />
        <path d="M 400 110 L 560 160 L 560 220 L 400 270 Z" fill="url(#funnelFill)" opacity="0.85" />

        {/* Stage 1, inventory (left, tall) */}
        <rect x="30" y="60" width="190" height="260" rx="10" fill="var(--surface)" stroke="var(--border-2)" />
        <text x="46" y="86" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">Cloud inventory</text>
        <text x="46" y="104" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">aws + azure · read-only</text>
        <text x="46" y="148" fontFamily="var(--mono)" fontSize="30" fontWeight="600" fill="var(--text)" letterSpacing="-0.02em">38,204</text>
        <text x="46" y="166" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.2" fill="var(--text-4)">RESOURCES · IDENTITIES</text>
        {/* mini grid of dots indicating mass */}
        <g fill="var(--text-4)" opacity="0.55">
          {Array.from({ length: 7 * 16 }).map((_, i) => {
            const c = i % 16, r = Math.floor(i / 16);
            return <circle key={i} cx={48 + c * 10} cy={196 + r * 14} r="1.4" />;
          })}
        </g>

        {/* Stage 2, raw signals (medium) */}
        <rect x="242" y="110" width="170" height="160" rx="10" fill="var(--surface)" stroke="var(--border-2)" />
        <text x="258" y="134" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">Raw signals</text>
        <text x="258" y="152" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">policy · anomaly · graph</text>
        <text x="258" y="196" fontFamily="var(--mono)" fontSize="26" fontWeight="600" fill="var(--text)" letterSpacing="-0.02em">1,017</text>
        <text x="258" y="214" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.2" fill="var(--text-4)">BEFORE RANKING</text>
        <line x1="258" y1="234" x2="394" y2="234" stroke="var(--border)" />
        <text x="258" y="250" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">most are noise</text>

        {/* Stage 3, ranked (smaller, cyan-tinged) */}
        <rect x="422" y="140" width="138" height="100" rx="10" fill="var(--surface-2)" stroke="var(--teal)" strokeOpacity="0.45" />
        <text x="438" y="164" fontFamily="var(--sans)" fontSize="11" fontWeight="600" fill="var(--text)">Ranked by risk</text>
        <text x="438" y="182" fontFamily="var(--mono)" fontSize="9" letterSpacing="0.6" fill="var(--text-4)">REACH · ID · DATA</text>
        {/* tiny ranked rows */}
        <g fontFamily="var(--mono)" fontSize="9" fill="var(--text-2)">
          <rect x="438" y="198" width="100" height="3" fill="var(--red)" opacity="0.85" />
          <rect x="438" y="208" width="82" height="3" fill="var(--red)" opacity="0.7" />
          <rect x="438" y="218" width="60" height="3" fill="var(--amber)" opacity="0.7" />
          <rect x="438" y="228" width="38" height="3" fill="var(--blue)" opacity="0.55" />
        </g>

        {/* Stage 4, the few (right, smallest, glow) */}
        <rect x="572" y="150" width="86" height="80" rx="10" fill="var(--surface-2)" stroke="var(--teal)" />
        <text x="588" y="194" fontFamily="var(--mono)" fontSize="30" fontWeight="600" fill="var(--teal)" letterSpacing="-0.02em">12</text>
        <text x="615" y="216" textAnchor="middle" fontFamily="var(--mono)" fontSize="8" letterSpacing="0.6" fill="var(--text-2)">WORTH FIXING</text>
        <circle cx="650" cy="160" r="3" fill="var(--teal)" />
        <circle cx="650" cy="160" r="10" fill="var(--teal)" opacity="0.18" />

        {/* Connectors with funneling pulse */}
        <path d="M 220 190 L 242 190" stroke="url(#line2)" strokeWidth="1.25" fill="none" />
        <path d="M 412 190 L 422 190" stroke="url(#line2)" strokeWidth="1.25" fill="none" />
        <path d="M 560 190 L 572 190" stroke="url(#line2)" strokeWidth="1.25" fill="none" />

        {/* small caption strip below */}
        <text x="30" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">collected continuously</text>
        <text x="242" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">most: noise, suppressed</text>
        <text x="422" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">scored every 90s</text>
        <text x="572" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--teal)">your morning</text>
      </svg>
    </div>
  );
}

function CoverageList() {
  const rows = [
    { chip: "AWS", chipCls: "aws", main: "IAM, S3, EC2, RDS, Lambda, EKS, KMS, CloudTrail", sub: "read-only · onboards via role ARN · zero agents", count: "feeds graph" },
    { chip: "AZURE", chipCls: "azure", main: "Entra ID, Storage, VMs, AKS, Key Vault, Defender, Monitor", sub: "read-only · app registration · multi-tenant", count: "feeds graph" },
    { chip: "RISK", chipCls: "", main: "Unified risk engine: reach, identity, blast radius, exploitability", sub: "one score across AWS + Azure, refreshed every 90s", count: "→ ranked" },
    { chip: "SSO", chipCls: "", main: "Entra ID, Azure AD, AWS IAM Identity Center, SAML 2.0", sub: "identity reach · used in risk", count: "—" },
    { chip: "OUT", chipCls: "", main: "Slack, Teams, Email, S3, Kafka, dashboards", sub: "only the ranked exposures, not the noise", count: "—" },
  ];
  return (
    <div className="cov-list">
      {rows.map((r, i) => (
        <div key={i} className="cov-row">
          <span className={"chip " + r.chipCls}>{r.chip}</span>
          <div>
            <div className="main">{r.main}</div>
            <div className="sub">{r.sub}</div>
          </div>
          <span className="count">{r.count}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------- Pipeline ---------- */
function Pipeline() {
  return (
    <section id="pipeline" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 03 — How it works</span></div>
            <h2>From cloud event to ranked exposure, in under a minute.</h2>
            <p style={{ marginTop: 12 }}>
              No agents, no log forwarders, no month-long rollouts. AtmosGuard reads what the
              cloud already records, builds a graph of what reaches what, and ranks each
              exposure by what it would actually cost you if it broke.
            </p>
          </div>
          <div className="micro" style={{ color: "var(--text-3)" }}>CONNECT → INGEST → DETECT → RESPOND</div>
        </div>
        <div className="pipeline">
          <Step num="01" title="Connect" desc="Read-only role in AWS; app registration in Azure. No data leaves your cloud boundary unencrypted." chips={["role ARN", "SAML", "SCIM"]} />
          <Step num="02" title="Ingest" desc="CloudTrail, Azure Activity, Config, identity and network state pulled continuously. Snapshots and diffs, not log forwarding." chips={["snapshots", "diffs", "streams"]} />
          <Step num="03" title="Detect & Correlate" desc="Every resource, identity, and edge assembled into a graph. Exposures scored by reach × data class × path to the internet, correlated across accounts." chips={["graph", "correlate", "score"]} active />
          <Step num="04" title="Respond" desc="Findings post to Slack or Teams with the resource, the identity, and the call attached. Acknowledge, escalate, or mark fixed in-thread, with signed evidence on the way out." chips={["slack", "teams", "attest"]} />
        </div>
      </div>
    </section>
  );
}
function Step({ num, title, desc, chips, active }) {
  return (
    <div className={"pipe-step" + (active ? " active" : "")}>
      <div className="num">{num}</div>
      <h4>{title}</h4>
      <p>{desc}</p>
      <div className="chip-row">
        {chips.map((c, i) => (<span key={i} className={"pill muted"}>{c}</span>))}
      </div>
    </div>
  );
}

/* ---------- Frameworks ---------- */
function Frameworks() {
  const items = [
    { name: "CIS", ctrl: "110 benchmarks", pct: 97, note: "AWS + Azure, Level 2" },
    { name: "PCI-DSS 4.0", ctrl: "264 requirements", pct: 92, note: "Level 1 ready" },
    { name: "SOC 2 Type II", ctrl: "146 controls", pct: 96, note: "Trust Services" },
    { name: "HIPAA", ctrl: "64 safeguards", pct: 89, note: "Security Rule", warn: true },
    { name: "ISO 27001:2022", ctrl: "93 controls", pct: 94, note: "Annex A" },
    { name: "NIST 800-53", ctrl: "310 controls", pct: 88, note: "Moderate baseline", warn: true },
  ];
  return (
    <section id="compliance" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 04 — Frameworks</span></div>
            <h2>The two controls that would fail your audit, named.</h2>
            <p style={{ marginTop: 12 }}>
              We don't show you 1,457 green checks. We show you the small set of controls
              whose evidence is stale, whose owner is unclear, or whose configuration drifted
              this week, and what to do about each before audit time.
            </p>
          </div>
          <div className="micro" style={{ color: "var(--text-3)" }}>2 AT RISK · ACROSS 6 FRAMEWORKS</div>
        </div>
        <div className="frameworks">
          {items.map((f, i) => (
            <div key={i} className={"fw " + (f.warn ? "warn" : "")}>
              <div className="label-line">
                <span className="name">{f.name}</span>
                <span className="pill muted">{f.note}</span>
              </div>
              <div className="ctrl mono">{f.ctrl}</div>
              <div className="bar"><span style={{ width: f.pct + "%" }} /></div>
              <div className="label-line">
                <span className="pct">{f.pct}%</span>
                <span className="micro">COVERAGE</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Docs / changelog ---------- */
function Docs() {
  return (
    <section id="docs" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 07 — Developers</span></div>
            <h2>Everything has an API. Everything has a log line.</h2>
            <p style={{ marginTop: 12 }}>
              A typed HTTP API, a CLI, and Terraform for the platform itself. Every finding has a
              permalink; every policy decision is reproducible.
            </p>
          </div>
          <div className="micro" style={{ color: "var(--text-3)" }}>API v3 · CLI 2.8.1</div>
        </div>
        <div className="docs-grid">
          <div className="code-card">
            <div className="tab-row">
              <div className="tab active">curl</div>
              <div className="tab">python</div>
              <div className="tab">terraform</div>
              <div className="tab tab-spacer" style={{ textAlign: "right" }}>api.atmosguard.io / v3</div>
            </div>
            <pre>
              <span className="c"># list open critical findings across AWS + Azure</span>{"\n"}
              <span className="fn">curl</span> <span className="o">-sS</span> <span className="s">"https://api.atmosguard.io/v3/findings"</span> <span className="o">\</span>{"\n"}
              {"  "}<span className="o">-H</span> <span className="s">"authorization: Bearer $AG_TOKEN"</span> <span className="o">\</span>{"\n"}
              {"  "}<span className="o">--data-urlencode</span> <span className="s">"severity=high"</span> <span className="o">\</span>{"\n"}
              {"  "}<span className="o">--data-urlencode</span> <span className="s">"status=open"</span>{"\n\n"}
              <span className="c"># → 1,017 findings · avg latency 74ms</span>{"\n"}
              <span className="k">{"{"}</span>{"\n"}
              {"  "}<span className="n">"id"</span>: <span className="s">"AG-48211"</span>,{"\n"}
              {"  "}<span className="n">"resource"</span>: <span className="s">"s3://prod-telemetry-log-archive"</span>,{"\n"}
              {"  "}<span className="n">"cloud"</span>: <span className="s">"aws"</span>,{"\n"}
              {"  "}<span className="n">"rule"</span>: <span className="s">"AWS.S3.PublicRead"</span>,{"\n"}
              {"  "}<span className="n">"severity"</span>: <span className="s">"high"</span>,{"\n"}
              {"  "}<span className="n">"first_seen"</span>: <span className="s">"2026-04-22T05:17:02Z"</span>,{"\n"}
              {"  "}<span className="n">"owner"</span>: <span className="s">"platform@northwind.example"</span>{"\n"}
              <span className="k">{"}"}</span>
            </pre>
          </div>
          <Changelog />
        </div>
      </div>
    </section>
  );
}
function Changelog() {
  const rows = [
    { date: "Apr 24", tag: "teal", label: "RELEASE", text: "Risk scoring v2, identity-reach weights tuned for AWS SSO + Entra." },
    { date: "Apr 18", tag: "blue", label: "API", text: "v3 adds /evidence · signed attestations for SOC 2 and ISO." },
    { date: "Apr 09", tag: "amber", label: "POLICY", text: "HIPAA 164.312(c)(2) mapping updated. Customers notified." },
    { date: "Mar 28", tag: "teal", label: "FEATURE", text: "Identity graph, lateral-movement chains within each cloud, refreshed every 90s." },
    { date: "Mar 14", tag: "purple", label: "SECURITY", text: "SOC 2 Type II renewed · Q1 report available in the portal." },
  ];
  return (
    <aside id="changelog" className="changelog">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span className="dot live" />
        <span className="micro">CHANGELOG · LIVE</span>
        <span className="micro" style={{ marginLeft: "auto", color: "var(--text-4)" }}>30d</span>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="row">
          <span className="date">{r.date}</span>
          <span className={"pill " + r.tag}>{r.label}</span>
          <span>{r.text}</span>
        </div>
      ))}
    </aside>
  );
}

/* ---------- CTA + Footer ---------- */
function CTA() {
  return (
    <section id="cta" className="cta-section">
      <div className="wrap-wide">
        <div className="rail-title" style={{ justifyContent: "center" }}>
          <span className="micro">§ END — GET STARTED</span>
        </div>
        <h2>Stop reading dashboards. Start fixing what matters.</h2>
        <span className="mono-sub">30-minute demo · we'll rank a sandbox account live · no slides</span>
        <div className="cta-row">
          <a className="btn primary lg" href="https://app.atmosguard.io/register">Start your free trial</a>
          <a className="btn lg" href={L("pricing.html")}>See pricing</a>
        </div>
        <div style={{ marginTop: 26, fontFamily: "var(--mono)", fontSize: 11, color: "var(--text-4)" }}>
          SOC 2 TYPE II · ISO 27001 · HIPAA · PCI DSS L1 · CIS · NIST 800-53
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="wrap-wide footer-inner">
        <div className="foot-brand">
          <a href={L("index.html")} className="brand"><BrandMark />AtmosGuard</a>
          <p>Cloud security, risk-first. AtmosGuard ranks every exposure across AWS and Azure by what it would actually cost you, so the top of your queue is the thing worth fixing today.</p>
        </div>
        <div>
          <h5>Product</h5>
          <ul>
            {PRODUCTS.map((p) => (
              <li key={p.id}><a href={L(p.href)}>{p.name} <span style={{ color: "var(--text-4)" }}>· {p.category}</span></a></li>
            ))}
            <li><a href={L("integrations.html")}>Integrations</a></li>
            <li><a href={L("pricing.html")}>Pricing</a></li>
          </ul>
        </div>
        <div>
          <h5>Company</h5>
          <ul>
            <li><a href={L("company.html#about")}>About</a></li>
            <li><a href={L("company.html#leadership")}>Leadership</a></li>
            <li><a href={L("careers.html")}>Careers</a></li>
            <li><a href={L("contact.html")}>Contact</a></li>
            <li><a href={L("blog/index.html")}>Blog</a></li>
          </ul>
        </div>
        <div>
          <h5>Legal</h5>
          <ul>
            <li><a href={L("privacy.html")}>Privacy</a></li>
            <li><a href={L("terms.html")}>Terms</a></li>
          </ul>
        </div>
      </div>
      <div className="wrap-wide foot-bottom">
        <span>© 2026 ATMOSGUARD, INC. · ALL TELEMETRY RESERVED</span>
      </div>
    </footer>
  );
}

/* ---------- Tweaks ---------- */
const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "live_clock": true,
  "density": "comfortable"
}/*EDITMODE-END*/;

function Tweaks() {
  const [state, setTweak] = useTweaks(TWEAK_DEFAULTS);
  // Force-clear any legacy data-accent value that persisted from earlier sessions.
  useEffect(() => {
    document.documentElement.removeAttribute("data-accent");
  }, []);
  return (
    <TweaksPanel title="Tweaks">
      <TweakSection label="Telemetry">
        <TweakToggle
          label="Live clock in nav"
          checked={state.live_clock}
          onChange={(v) => setTweak("live_clock", v)}
        />
      </TweakSection>
      <TweakSection label="Layout">
        <TweakRadio
          label="Density"
          value={state.density}
          onChange={(v) => setTweak("density", v)}
          options={[
            { value: "comfortable", label: "Comfy" },
            { value: "compact", label: "Compact" },
          ]}
        />
      </TweakSection>
    </TweaksPanel>
  );
}

/* ---------- Page chrome + section pickers ---------- */
function Page({ active, children }) {
  return (<>
    <Nav active={active} />
    {children}
    <CTA />
    <Footer />
    <Tweaks />
  </>);
}

const DEMO_API_ENDPOINT = 'https://7cjtuz49li.execute-api.us-east-1.amazonaws.com/interest';

/* Page-level mini hero, shared header strip used on inner pages */
function PageHeader({ kicker, title, lead, meta, cta, strip, productLogo }) {
  return (
    <section className="page-hero">
      <div className="wrap-wide" style={{ position: "relative" }}>
        <div className="rail-title">
          {productLogo && <ProductLogo kind={productLogo} size={20} />}
          <span className="micro">{kicker}</span>
          <span style={{ marginLeft: "auto" }} className="micro" >{meta}</span>
        </div>
        <div className="page-hero-grid">
          <h1 className="page-h1">{title}</h1>
          <div className="page-hero-side">
            <p className="page-lead">{lead}</p>
            {cta && <div className="hero-cta" style={{ marginTop: 22 }}>{cta}</div>}
          </div>
        </div>
        {strip && (
          <div className="page-hero-strip">
            {strip.map((s, i) => (
              <div key={i} className="ph-cell">
                <span className="ph-k">{s.k}</span>
                <span className="ph-v">{s.v}</span>
                {s.s && <span className="ph-s">{s.s}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ─── Compliance, screenshot frame (wraps an inline-SVG product screen from compliance-screens.jsx) ─── */
function ComplianceShot({ path, right, children }) {
  const parts = String(path).split("/");
  return (
    <div className="shot">
      <div className="console-bar">
        <span className="dot live" />
        <span className="path">app.atmosguard.io</span>
        {parts.map((p, i) => (
          <React.Fragment key={i}>
            <span className="sep">/</span>
            <span className="path">{p}</span>
          </React.Fragment>
        ))}
        {right && <span className="right"><span className="mono" style={{ color: "var(--text-3)" }}>{right}</span></span>}
      </div>
      {children}
    </div>
  );
}

/* ─── Compliance, Audit timeline ─── */
function AuditTimeline() {
  const phases = [
    { n: "Q-3", k: "Steady state", days: "everyday", desc: "Evidence collected continuously. Exceptions tracked with reviewer attribution. No prep, this is the audit-ready posture.", color: "teal" },
    { n: "Q-2", k: "Scoping", days: "30 minutes", desc: "Select frameworks, scope subsidiaries, freeze the control library. Generated diff vs. last audit, reviewed in 30 minutes.", color: "teal" },
    { n: "Q-1", k: "Walk-through", days: "1 day", desc: "Auditor opens the console. Drills into any control. Verifies through collected evidence. They are done by lunch.", color: "teal" },
    { n: "Q-0", k: "Report", days: "≤ 30d", desc: "Auditor issues report. Findings (if any) auto-route to control owners with remediation suggestions. Closed by next quarter.", color: "amber" },
  ];
  return (
    <div className="audit-timeline">
      <div className="audit-rail" aria-hidden="true" />
      {phases.map((p, i) => (
        <div key={i} className="audit-phase">
          <div className="audit-dot" />
          <div className="audit-card">
            <div className="audit-head">
              <span className={"pill " + (p.color === "amber" ? "amber" : "teal")}>{p.k}</span>
              <span style={{ marginLeft: "auto" }} className="micro">{p.days}</span>
            </div>
            <p className="audit-desc">{p.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}


/* ─── Compliance, Evidence flow diagram ─── */
function EvidenceFlow() {
  const stages = [
    { n: "01", k: "Source", t: "Cloud APIs", d: "Read-only role, every account. CloudTrail, Azure Activity, Config." },
    { n: "02", k: "Collect", t: "Continuous capture", d: "Snapshots, configs, diffs. Idempotent, re-runnable on demand." },
    { n: "03", k: "Compile", t: "Per framework", d: "Findings grouped by control. Owner, status, and evidence references attached." },
    { n: "04", k: "Export", t: "PDF report", d: "One PDF per framework. Cover page, findings, evidence appendix, reviewer summary." },
    { n: "05", k: "Deliver", t: "Auditor, offline", d: "Hand them the PDF. They open it, read it, and stop emailing your team at 11pm." },
  ];
  return (
    <div className="ev-flow">
      {stages.map((s, i) => (
        <div key={i} className="ev-stage">
          <div className="ev-stage-head">
            <span className="micro">§ {s.n}</span>
            <span className="pill muted">{s.k}</span>
          </div>
          <div className="ev-stage-title">{s.t}</div>
          <p className="ev-stage-desc">{s.d}</p>
          {i < stages.length - 1 && <div className="ev-stage-arrow" aria-hidden="true">→</div>}
        </div>
      ))}
    </div>
  );
}

function FeatureCard({ label, title, desc, stat, statLbl, minHeight = 320 }) {
  return (
    <div className="module" style={{ minHeight }}>
      <div className="mod-head">
        <span className="pill teal">{label}</span>
      </div>
      <h3>{title}</h3>
      <p className="mod-desc">{desc}</p>
      <div className="mod-vis" style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
        <div className="mono" style={{ fontSize: 24, color: "var(--text)", letterSpacing: "-0.01em", fontFeatureSettings: '"tnum"' }}>{stat}</div>
        <span className="micro">{statLbl}</span>
      </div>
    </div>
  );
}

/* ─── Pricing page (contact-for-quote, Wiz-style split layout) ─── */

const PRICING_LICENSES = [
  {
    id: "atlas",
    name: "Atlas",
    tag: "CNAPP",
    scale: "Scales with monitored resources across AWS and Azure",
    summary: "Continuous misconfiguration coverage across AWS and Azure: drift detection, exposure mapping, and risk-ranked findings.",
    includes: [
      "Multi-account AWS & Azure coverage",
      "On-demand, scheduled, and continuous scan modes",
      "Identity, network, storage, and logging checks",
      "Risk-ranked queue with blast-radius context",
      "Exports and audit-ready evidence trails",
    ],
  },
  {
    id: "forge",
    name: "Forge",
    tag: "APPSEC",
    scale: "Scales with connected repositories and scan volume",
    summary: "Code flaws, leaked secrets, infrastructure templates, and risky dependencies scanned on every push, ranked by what actually runs in production.",
    includes: [
      "GitHub app, read-only, every connected repo",
      "Four engines: code, secrets, infrastructure, dependencies",
      "Reachability ranking against what your repos deploy",
      "Leaked credentials verified live before they page anyone",
      "Fix pull requests your team reviews and merges",
    ],
  },
  {
    id: "sentry",
    name: "Sentry",
    tag: "THREAT INTEL",
    soon: true,
    scale: "Scales with monitored resources and security telemetry",
    summary: "Cloud-native SIEM with AI-powered investigations, correlation rules, and a live event feed built for cloud telemetry.",
    includes: [
      "AWS & Azure log ingestion and normalization",
      "AI-assisted investigations and correlation",
      "Custom detection rules and alert routing",
      "Retention tiers matched to your compliance needs",
      "Integrations with Slack, PagerDuty, and webhooks",
    ],
  },
  {
    id: "stratos",
    name: "Stratos",
    tag: "AI GOVERNANCE",
    soon: true,
    scale: "Scales with models and agents in scope",
    summary: "An inventory of the models and agents in use, an audit trail for every call, and policy guardrails reviewed like any other change. Coming soon to private preview.",
    includes: [
      "Model and agent inventory across cloud and repos",
      "Per-call audit trail, time-stamped on capture",
      "Policy guardrails as code, reviewed in pull requests",
      "Exports for auditors and risk committees",
      "Private preview onboarding",
    ],
  },
];

function PricingPage() {
  return (<>
    <PageHeader
      kicker="§ PRICING REQUEST"
      title={<>Platform pricing,<br /><span style={{ color: "var(--teal)" }}>built for your stack.</span></>}
      lead="Licensing is modular, scaling with monitored resources, security telemetry, and compliance frameworks. Learn about AtmosGuard licenses below, then request a custom quote."
      meta="CUSTOM QUOTES"
      strip={[
        { k: "Products", v: "4", s: "Atlas · Forge · Sentry · Stratos" },
        { k: "Clouds", v: "AWS + Azure", s: "multi-account" },
        { k: "Evaluation", v: "Pilot", s: "hands-on, no slides" },
      ]}
    />
    <PricingSplitLayout />
    <PricingScaleSection />
  </>);
}

function PricingLicenseAccordion() {
  const [open, setOpen] = React.useState("atlas");
  return (
    <div className="pricing-licenses">
      <p className="pricing-licenses-lead">
        AtmosGuard licenses are modular. Each product scales independently. Pick what you need and we&apos;ll size a quote to your environment.
      </p>
      <div className="license-accordion">
        {PRICING_LICENSES.map((lic) => {
          const isOpen = open === lic.id;
          return (
            <div key={lic.id} className={"license-item" + (isOpen ? " open" : "")}>
              <button
                type="button"
                className="license-trigger"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? null : lic.id)}
              >
                <span className="license-trigger-main">
                  <span className={"pill " + (lic.soon ? "amber" : "muted")}>{lic.soon ? lic.tag + " · SOON" : lic.tag}</span>
                  <span className="license-name">{lic.name}</span>
                </span>
                <span className="license-caret" aria-hidden="true">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="license-panel">
                  <p className="license-summary">{lic.summary}</p>
                  <div className="license-scale">
                    <span className="dot live" />
                    <span className="mono">{lic.scale}</span>
                  </div>
                  <ul className="license-includes">
                    {lic.includes.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  <p className="license-cta-note">
                    <span className="mono" style={{ color: "var(--teal)" }}>Contact us for pricing →</span>
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="pricing-social">
        <p className="pricing-social-stat">
          Security teams across <span className="mono" style={{ color: "var(--text)" }}>AWS</span> and <span className="mono" style={{ color: "var(--text)" }}>Azure</span> use AtmosGuard to cut signal-to-noise and close audit gaps faster.
        </p>
        <div className="pricing-social-logos">
          {["FIN · REGULATED", "SAAS · SOC 2", "HEALTH · HIPAA", "RETAIL · PCI"].map((l, i) => (
            <span key={i} className="pricing-logo-chip mono">{l}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function PricingRequestForm() {
  const TOTAL_STEPS = 3;
  const [step, setStep] = React.useState(1);
  const [email, setEmail] = React.useState("");
  const [products, setProducts] = React.useState({ atlas: false, forge: false, sentry: false, stratos: false });
  const [details, setDetails] = React.useState({ firstName: "", lastName: "", jobTitle: "", company: "", phone: "" });
  const setDetail = (k) => (e) => setDetails(s => ({ ...s, [k]: e.target.value }));
  const [status, setStatus] = React.useState("idle");
  const [errorMsg, setErrorMsg] = React.useState("");
  const formRef = React.useRef(null);

  const anyProduct = Object.values(products).some(Boolean);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const toggleProduct = (id) => setProducts(s => ({ ...s, [id]: !s[id] }));

  async function onSubmit(e) {
    e.preventDefault();
    if (status === "submitting" || !anyProduct) return;
    setStatus("submitting");
    setErrorMsg("");
    const fd = new FormData(e.target);
    const selected = Object.entries(products).filter(([, v]) => v).map(([k]) => PRICING_LICENSES.find(l => l.id === k)?.name ?? k).join(", ");
    const data = {
      source: "pricing-page",
      email: email.trim(),
      name: [details.firstName, details.lastName].map(s => s.trim()).filter(Boolean).join(" "),
      company: details.company.trim(),
      role: details.jobTitle.trim(),
      phone: details.phone.trim(),
      topic: "pricing",
      message: [
        `Products: ${selected || "not specified"}`,
        fd.get("notes") ? `Notes: ${fd.get("notes")}` : "",
      ].filter(Boolean).join("\n"),
      website: fd.get("website"),
    };
    try {
      const res = await fetch(DEMO_API_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (result && result.ok) setStatus("success");
      else throw new Error((result && result.error) || "Submission failed");
    } catch (err) {
      setErrorMsg(err.message || "Network error. Please try again.");
      setStatus("error");
    }
  }

  const submitting = status === "submitting";

  if (status === "success") {
    return (
      <div className="pricing-request-card">
        <div className="pricing-request-success">
          <div className="pricing-request-success-icon">✓</div>
          <h3>Quote request received.</h3>
          <p>
            Logged as <span className="mono" style={{ color: "var(--teal)" }}>quote-{Math.random().toString(36).slice(2, 8).toUpperCase()}</span>.
            A founder or engineer will follow up within two business days with a custom quote.
          </p>
          <button type="button" className="btn" onClick={() => { setStatus("idle"); setStep(1); setEmail(""); setProducts({ atlas: false, forge: false, sentry: false, stratos: false }); setDetails({ firstName: "", lastName: "", jobTitle: "", company: "", phone: "" }); }}>
            Submit another request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pricing-request-card">
      <div className="pricing-request-head">
        <span className="dot live" />
        <span className="mono" style={{ color: "var(--text-2)", fontSize: 12 }}>atmosguard / pricing / quote-request</span>
      </div>
      <form ref={formRef} onSubmit={onSubmit} className="pricing-request-form" noValidate>
        <input type="text" name="website" tabIndex={-1} autoComplete="off"
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} aria-hidden="true" />

        <div className="pricing-request-progress">
          <div className="pricing-request-progress-bar">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div key={i} className={"pr-dot" + (i < step ? " active" : "")} />
            ))}
          </div>
          <span className="mono pricing-request-step">Step {step} of {TOTAL_STEPS}</span>
        </div>

        {step === 1 && (
          <div className="pricing-request-step-body">
            <h3>Your details</h3>
            <p className="pricing-request-hint">Please enter your work email below</p>
            <label style={{ display: "block" }}>
              <span className="cf-label mono">Work Email <span style={{ color: "var(--red)" }}>*</span></span>
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                style={fieldStyle()}
              />
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="pricing-request-step-body">
            <h3>Tell us about you</h3>
            <p className="pricing-request-hint">So we can tailor the quote to your team</p>
            <div className="cf-row cf-row-2">
              <Field label="First Name" name="firstName" required value={details.firstName} onChange={setDetail("firstName")} />
              <Field label="Last Name" name="lastName" required value={details.lastName} onChange={setDetail("lastName")} />
            </div>
            <div className="cf-row cf-row-2" style={{ marginTop: 14 }}>
              <Field label="Job Title" name="jobTitle" required placeholder="e.g. CISO, Head of Cloud" value={details.jobTitle} onChange={setDetail("jobTitle")} />
              <Field label="Company" name="company" required value={details.company} onChange={setDetail("company")} />
            </div>
            <div className="cf-row" style={{ marginTop: 14 }}>
              <Field label="Phone" name="phone" type="tel" placeholder="+1 · optional" value={details.phone} onChange={setDetail("phone")} />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="pricing-request-step-body">
            <h3>What are you evaluating?</h3>
            <p className="pricing-request-hint">Select the products you want to evaluate</p>
            <div className="cf-row">
              <span className="cf-label mono">Products of interest <span style={{ color: "var(--red)" }}>*</span></span>
              <div className="cf-pills" style={{ marginTop: 8 }}>
                {PRICING_LICENSES.map((lic) => (
                  <button key={lic.id} type="button"
                    onClick={() => toggleProduct(lic.id)}
                    className={"pill " + (products[lic.id] ? "teal" : "muted")}
                    style={{ cursor: "pointer", border: "1px solid", borderColor: products[lic.id] ? "color-mix(in oklab, var(--teal) 35%, transparent)" : "var(--border)" }}>
                    {products[lic.id] ? "✓ " : ""}{lic.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="cf-row" style={{ marginTop: 14 }}>
              <label style={{ display: "block" }}>
                <span className="cf-label mono">Additional context</span>
                <textarea name="notes" rows={3}
                  placeholder="Compliance frameworks in scope, timeline, anything else we should know."
                  style={fieldStyle()} />
              </label>
            </div>
          </div>
        )}

        {status === "error" ? <div className="cf-error">{errorMsg || "Something went wrong."}</div> : null}

        <div className="pricing-request-nav">
          {step > 1 ? (
            <button type="button" className="wiz-back" onClick={() => setStep(s => s - 1)}>← Back</button>
          ) : <span />}
          {step < TOTAL_STEPS ? (
            <button
              type="button"
              className="btn primary lg pricing-request-next"
              disabled={step === 1 ? !emailValid : false}
              onClick={() => setStep(s => s + 1)}
            >
              Next →
            </button>
          ) : (
            <button type="submit" className="btn primary lg pricing-request-next" disabled={submitting || !anyProduct}>
              {submitting ? "Sending…" : "Request a quote"}
            </button>
          )}
        </div>

        <p className="pricing-request-foot mono">
          Personalised quote · a sales rep will be in touch
        </p>
      </form>
    </div>
  );
}

function PricingSplitLayout() {
  return (
    <section className="section" id="quote">
      <div className="wrap-wide">
        <div className="pricing-split">
          <PricingLicenseAccordion />
          <PricingRequestForm />
        </div>
      </div>
    </section>
  );
}

function PricingScaleSection() {
  const factors = [
    { k: "Atlas", v: "Resources monitored", s: "Trial · Starter · Professional · Enterprise" },
    { k: "Forge", v: "Repositories connected", s: "Trial · Starter · Professional · Enterprise" },
    { k: "Sentry", v: "Resources + Log volume", s: "Private preview · Enterprise" },
    { k: "Stratos", v: "Models + agents in scope", s: "Private preview · Enterprise" },
  ];
  return (
    <section className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ HOW LICENSING WORKS</span></div>
            <h2>What your quote is based on.</h2>
            <p style={{ marginTop: 12 }}>
              We don&apos;t publish list prices. Every environment is different. Your quote reflects actual scope: resources monitored, frameworks, and retention. No shelf-ware, no surprise overages on renewal.
            </p>
          </div>
          <div className="micro" style={{ color: "var(--text-3)" }}>CONTACT US · CUSTOM QUOTE</div>
        </div>
        <div className="stats-strip pricing-scale-grid">
          {factors.map((f, i) => (
            <div key={i} className="stat">
              <span className="micro">{f.k}</span>
              <div className="val">{f.v}</div>
              <div className="delta teal">{f.s}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 28, textAlign: "center" }}>
          <a className="btn primary lg" href="#quote">Request a custom quote</a>
          <p className="micro" style={{ marginTop: 12, color: "var(--text-3)" }}>
            Prefer email? <a href="mailto:info@atmosguard.io" style={{ color: "var(--teal)", textDecoration: "none" }}>info@atmosguard.io</a>
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Integrations page ─── */
function IntegrationsPage() {
  const categories = [
    {
      title: "Cloud Providers",
      slug: "cloud-providers",
      integrations: [
        {
          id: "aws",
          name: "AWS",
          status: "available",
          blurb: "Deep integration with Amazon Web Services for continuous security assessment across EC2, S3, Lambda, RDS, and other AWS services. Automatically monitor your entire AWS infrastructure for misconfigurations, vulnerabilities, and compliance drift.",
          capsLabel: "Capabilities",
          caps: [
            "Real-time monitoring across all AWS services",
            "Automated compliance checking against AWS best practices",
            "Vulnerability detection and remediation guidance",
            "Audit trail integration with CloudTrail and EventBridge",
          ],
          cta: "Get started with AWS",
          mark: <AWSMark />,
        },
        {
          id: "azure",
          name: "Azure",
          status: "available",
          blurb: "Full integration with Microsoft Azure for comprehensive security monitoring across subscriptions, resource groups, and services. Continuously scan your Azure environment for configuration risks, compliance gaps, and security vulnerabilities.",
          capsLabel: "Capabilities",
          caps: [
            "Multi-subscription visibility and monitoring",
            "Azure Policy and Azure Security Center integration",
            "Identity and access management insights",
            "Compliance monitoring for Azure-specific frameworks",
          ],
          cta: "Get started with Azure",
          mark: <AzureMark />,
        },
      ],
    },
    {
      title: "Collaboration",
      slug: "collaboration",
      integrations: [
        {
          id: "slack",
          name: "Slack",
          status: "available",
          blurb: "Install the AtmosGuard app directly into your Slack workspace and run your security posture without leaving the conversation. Receive daily risk digests in the channels that matter, investigate findings with full context, and acknowledge or suppress risks inline.",
          capsLabel: "Capabilities",
          caps: [
            "Receive scheduled risk digests and real-time alerts",
            "Investigate findings with full context and evidence",
            "Suppress or acknowledge risks directly from Slack",
            "Route alerts to specific channels by severity or account",
          ],
          cta: "Get started with Slack",
          mark: <SlackMark />,
        },
        {
          id: "teams",
          name: "Microsoft Teams",
          status: "soon",
          blurb: "Bring AtmosGuard into Microsoft Teams and keep your security operations where your team already collaborates. Receive risk digests and alerts in Teams channels, investigate and triage findings inline, and suppress or escalate risks without switching tools. Built for organisations that run on the Microsoft ecosystem.",
          capsLabel: "Planned capabilities",
          caps: [
            "Receive scheduled risk digests and real-time alerts",
            "Investigate findings with full context and evidence",
            "Suppress or acknowledge risks directly from Teams",
            "Adaptive card formatting for rich security context",
          ],
          cta: "Join the waitlist",
          mark: <TeamsMark />,
        },
        {
          id: "guardduty",
          name: "Amazon GuardDuty",
          status: "soon",
          blurb: "Deep integration with AWS GuardDuty to correlate threat findings with your cloud posture and identity risks. Combine GuardDuty detections with AtmosGuard's risk scoring for comprehensive threat visibility across your AWS infrastructure.",
          capsLabel: "Planned capabilities",
          caps: [
            "Real-time GuardDuty finding ingestion and correlation",
            "Threat scoring combined with posture risk",
            "Cross-account threat visibility",
            "Automated response playbooks for common threat patterns",
          ],
          cta: "Join the waitlist",
          mark: <GuardDutyMark />,
        },
      ],
    },
    {
      title: "Source Code and SDLC",
      slug: "source-code-sdlc",
      integrations: [
        {
          id: "github",
          name: "GitHub",
          status: "available",
          blurb: "Connect AtmosGuard to your GitHub repositories for static application security testing (SAST), automated code scanning, and cross-layer correlation between infrastructure risks and application vulnerabilities. Surface security issues early in the development lifecycle.",
          capsLabel: "Capabilities",
          caps: [
            "SAST scanning on pull requests and merges",
            "Automated code scanning for security vulnerabilities",
            "Cross-layer correlation with cloud posture findings",
            "PR annotations with remediation guidance",
          ],
          cta: "Get started with GitHub",
          mark: <GitHubMark />,
        },
        {
          id: "jira",
          name: "Jira",
          status: "soon",
          blurb: "Seamlessly create and manage security issues directly from AtmosGuard findings in your Jira workspace. Automatically route findings to development teams, track remediation progress, and maintain audit trails of all security work across your organization.",
          capsLabel: "Planned capabilities",
          caps: [
            "Automatic ticket creation from security findings",
            "Custom field mapping and issue type configuration",
            "Bi-directional status sync with AtmosGuard",
            "Workflow automation and escalation rules",
          ],
          cta: "Join the waitlist",
          mark: <JiraMark />,
        },
      ],
    },
  ];
  return (<>
    <PageHeader
      kicker="§ INTEGRATIONS"
      title={<>Connect the tools<br /><span style={{ color: "var(--teal)" }}>your team already uses.</span></>}
      lead="Security findings reach the right people at the right time, without context-switching. One-click setup, real-time alerts, and investigation from anywhere your team already operates."
      meta="ONE-CLICK SETUP · REAL-TIME · INVESTIGATE FROM ANYWHERE"
      strip={[
        { k: "Available today", v: "04", s: "Slack · GitHub · AWS · Azure" },
        { k: "On the roadmap", v: "02", s: "Teams · Jira · PagerDuty" },
        { k: "Setup time", v: "< 5m", s: "OAuth · zero infra" },
        { k: "Event lag", v: "≤ 90s", s: "finding → channel" },
      ]}
    />

    {/* Available + roadmap by category */}
    {categories.map(cat => (
      <section key={cat.slug} className="section">
        <div className="wrap-wide">
          <div className="section-head">
            <div className="lead">
              <div className="rail-title"><span className="micro">§ {cat.title.toUpperCase()}</span></div>
              <h2>{cat.title}</h2>
            </div>
          </div>
          <div className="modules">
            {cat.integrations.map(it => <IntegrationCard key={it.id} {...it} />)}
          </div>
        </div>
      </section>
    ))}

    {/* How integrations work */}
    <section className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ HOW IT WORKS</span></div>
            <h2>Up and running in minutes, not days.</h2>
          </div>
          <div className="micro" style={{ color: "var(--text-3)" }}>CONNECT → CONFIGURE → OPERATE</div>
        </div>
        <div className="modules">
          <FeatureCard
            label="01 · Connect"
            title="Authorise from the dashboard."
            desc="One click from the AtmosGuard dashboard. OAuth into Slack, Teams or GitHub, no agents, no infrastructure changes, nothing to install on your runners."
            stat="< 1m" statLbl="TIME TO AUTHORISE"
          />
          <FeatureCard
            label="02 · Configure"
            title="Choose what flows where."
            desc="Pick which alerts, digests, and actions route into each tool. Filter by severity, account, finding type, or business unit. Every rule is reproducible, exportable as JSON, version-pinned."
            stat="07" statLbl="ROUTING DIMENSIONS"
          />
          <FeatureCard
            label="03 · Operate"
            title="Investigate without switching."
            desc="Acknowledge, suppress, or escalate findings directly from Slack, Teams, or a GitHub PR comment. Every action is captured in the audit trail with the identity and the resource attached."
            stat="42" statLbl="ACTIONS PER WEEK"
          />
        </div>
      </div>
    </section>

    {/* Need a different integration? */}
    <section className="section">
      <div className="wrap-wide">
        <div className="integ-cta">
          <div className="integ-cta-text">
            <div className="rail-title"><span className="micro">§ ROADMAP</span></div>
            <h2 style={{ marginTop: 8 }}>Need a different integration?</h2>
            <p style={{ marginTop: 12, color: "var(--text-2)", maxWidth: "56ch" }}>We build new integrations based on what our pilot teams actually use. Tell us what's on your stack and we'll prioritise it against the roadmap.</p>
            <div className="integ-cta-actions">
              <a className="btn primary" href="#contact">Request an integration</a>
              <a className="btn ghost" href="#contact">Talk to us</a>
            </div>
          </div>
          <div className="integ-cta-side">
            <div className="micro" style={{ color: "var(--text-3)", marginBottom: 14 }}>CURRENTLY EVALUATING</div>
            <ul className="integ-roadmap-list">
              <li><span className="ir-name">Jira</span><span className="ir-status mono">Q1 2027 ROADMAP</span></li>
              <li><span className="ir-name">PagerDuty</span><span className="ir-status mono">Q1 2027 ROADMAP</span></li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </>);
}

function IntegrationCard({ name, status, blurb, capsLabel, caps, cta, mark }) {
  const isAvailable = status === "available";
  return (
    <div className="module integ-card" style={{ minHeight: 460 }}>
      <div className="mod-head" style={{ alignItems: "center", gap: 14 }}>
        <div className="integ-mark">{mark}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 17, color: "var(--text)", fontWeight: 600, letterSpacing: "-0.005em" }}>{name}</div>
          <span className={"pill " + (isAvailable ? "green" : "amber")}>
            {isAvailable
              ? <><span className="dot live" style={{ background: "var(--green)" }} />AVAILABLE</>
              : <>COMING SOON</>}
          </span>
        </div>
      </div>
      <p className="mod-desc" style={{ maxWidth: "none" }}>{blurb}</p>
      <div className="integ-caps">
        <div className="micro" style={{ color: "var(--text-3)", marginBottom: 10 }}>§ {capsLabel.toUpperCase()}</div>
        <ul className="integ-cap-list">
          {caps.map((c, i) => (
            <li key={i} className={isAvailable ? "" : "dim"}>
              <span className="integ-check" aria-hidden="true">{isAvailable ? "✓" : "·"}</span>
              <span>{c}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="integ-foot">
        <a className={"btn " + (isAvailable ? "primary" : "ghost")} href="#contact" style={{ width: "100%", justifyContent: "center" }}>{cta}</a>
      </div>
    </div>
  );
}

function SlackMark() {
  return (
    <img src={L("logos/slack.svg")} width="32" height="32" alt="" aria-hidden="true" style={{ display: "block" }} />
  );
}
function TeamsMark() {
  return (
    <img src={L("logos/ms-teams.svg")} width="32" height="32" alt="" aria-hidden="true" style={{ display: "block" }} />
  );
}
function GitHubMark() {
  return (
    <svg viewBox="0 0 98 96" width="30" height="30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fillRule="evenodd" clipRule="evenodd" d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0z" fill="#fff" />
    </svg>
  );
}

function AWSMark() {
  return (
    <img src={L("logos/aws.svg")} width="30" height="30" alt="" aria-hidden="true" style={{ display: "block" }} />
  );
}

function AzureMark() {
  return (
    <img src={L("logos/azure.svg")} width="30" height="30" alt="" aria-hidden="true" style={{ display: "block" }} />
  );
}

function JiraMark() {
  return (
    <img src={L("logos/jira.svg")} width="30" height="30" alt="" aria-hidden="true" style={{ display: "block" }} />
  );
}

function GuardDutyMark() {
  return (
    <img src={L("logos/guardduty.svg")} width="30" height="30" alt="" aria-hidden="true" style={{ display: "block" }} />
  );
}

/* ─── Pilot program page ─── */
function CustomersPage() {
  return (<>
    <PageHeader
      kicker="§ PILOT PROGRAM"
      title={<>A hands-on pilot,<br /><span style={{ color: "var(--teal)" }}>for a small number of teams.</span></>}
      lead="AtmosGuard is in private beta. We run hands-on pilots with a small group of teams at a time, weekly sessions with the founders, direct line to engineering, pricing locked for the duration of the program."
      meta="PILOT COHORT · BY INVITATION"
      strip={[
        { k: "Pilot length", v: "90 days", s: "price locked" },
        { k: "Founder sync", v: "weekly", s: "every Tuesday" },
        { k: "Onboarding", v: "< 1h", s: "read-only roles, no agents" },
        { k: "Cohort size", v: "small", s: "intentional, hands-on" },
      ]}
    />
    <section className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ HOW A PILOT WORKS</span></div>
            <h2>What you get, what we ask.</h2>
          </div>
        </div>
        <div className="modules">
          <CaseCard org="01 · Onboarding" pill="Week one" headline="Up and ranking risk in under an hour." stat="< 1h" statLbl="TIME TO FIRST FINDING" body="Read-only roles in AWS and Azure, deployed via Terraform or CloudFormation. No agents, no infrastructure changes, nothing to install on your runners. By the end of the day you have a ranked queue of real exposures from your environment." />
          <CaseCard org="02 · Operating model" pill="Ongoing" headline="Weekly with the founders, direct line to engineering." stat="weekly" statLbl="FOUNDER SYNC" body="Every Tuesday, we walk through what AtmosGuard surfaced, what landed in your queue, and what we're shipping next. Detection logic, integrations, and roadmap order are shaped in the room. You aren't a logo, you're a co-author." />
          <CaseCard org="03 · Outcome" pill="By day 90" headline="Decide together, on real evidence." stat="90 days" statLbl="PILOT LENGTH" body="At the end of the pilot you have a decision based on real data from your environment, not a sales deck. Pricing is locked from day one for the duration of the program, no surprise renewals, no aggressive upsell." />
        </div>
      </div>
    </section>
  </>);
}

function CaseCard({ org, pill, headline, stat, statLbl, body }) {
  return (
    <div className="module" style={{ minHeight: 320 }}>
      <div className="mod-head">
        <span className="pill teal">{pill}</span>
        <span className="micro mono" style={{ marginLeft: "auto" }}>{org.toUpperCase()}</span>
      </div>
      <h3>{headline}</h3>
      <p className="mod-desc">{body}</p>
      <div className="mod-vis" style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
        <div className="mono" style={{ fontSize: 24, color: "var(--text)", letterSpacing: "-0.01em", fontFeatureSettings: '"tnum"' }}>{stat}</div>
        <span className="micro">{statLbl}</span>
      </div>
    </div>
  );
}

/* ─── Docs page ─── */
function DocsPage() {
  return (<>
    <PageHeader
      kicker="§ DEVELOPERS"
      title={<>Everything has an API.<br /><span style={{ color: "var(--teal)" }}>Everything has a log line.</span></>}
      lead="A typed HTTP API, a CLI, and Terraform for the platform itself. Every finding has a permalink; every policy decision is reproducible."
      meta="API V3 · CLI 2.8.1"
      strip={[
        { k: "API version", v: "v3", s: "stable · since 2025-11" },
        { k: "CLI", v: "2.8.1", s: "darwin · linux · win" },
        { k: "Terraform", v: "hashicorp/atmos", s: "v0.14.2" },
        { k: "Outputs", v: "native", s: "Slack · Teams · Email" },
      ]}
    />
    <Docs />
  </>);
}

/* ─── Company / Careers ─── */
function CompanyPage() {
  return (<>
    <PageHeader
      kicker="§ COMPANY"
      title={<>AI-native cloud security,<br /><span style={{ color: "var(--teal)" }}>built where the clouds meet.</span></>}
      lead="AtmosGuard is an AI-native cloud security platform built by engineers who scaled infrastructure at AWS, Uber, and Careem. We correlate signals across your entire cloud estate so the few risks that actually matter rise to the top, without the noise, the agents, or the multi-tool sprawl."
      meta="FOUNDED 2025 · DUBAI · UAE"
      strip={[
        { k: "Founded", v: "2025", s: "Dubai · UAE" },
        { k: "Stage", v: "Pre-seed", s: "closing 2026 Q2" },
        { k: "Team", v: "07", s: "5 eng · 2 GTM" },
        { k: "Hiring", v: "4 roles", s: "see careers" },
      ]}
    />
    <section id="about" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ WHAT WE STAND FOR</span></div>
            <h2>Stop real cloud threats. Ignore the noise.</h2>
            <p style={{ marginTop: 12 }}>Modern cloud environments generate thousands of alerts a day across identity, infrastructure, applications, and networks. Most security tools add to the pile. AtmosGuard correlates across your entire stack so you see the few risks that require action, and the evidence to act on them.</p>
          </div>
        </div>
        <div className="stats-strip">
          <Stat label="ENGINEERED BY" val="Ex-AWS · Ex-Uber" delta="Careem · global scale" dcls="teal" sub="where the founders shipped" />
          <Stat label="SIGNAL-TO-NOISE" val="98.4%" delta="alerts deduped" dcls="up" sub="millions in → ranked queue out" />
          <Stat label="TIME TO ONBOARD" val="< 5m" delta="OAuth · zero agents" dcls="teal" sub="no infra changes" />
          <Stat label="STAGE" val="Pre-seed" delta="founded 2025" dcls="up" sub="Q3 cohort opening" />
        </div>
      </div>
    </section>
    <section id="leadership" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ LEADERSHIP</span></div>
            <h2>The people behind the platform.</h2>
            <p style={{ marginTop: 12 }}>Engineers who've built and secured infrastructure at the world's most demanding scale.</p>
          </div>
        </div>
        <div className="modules" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))", maxWidth: 980, margin: "0 auto", gap: 28 }}>
          <PersonCard img="images/Marwan.png" imgStyle={{ transform: "scale(2.2) translate(-15%, 28%)" }} name="Marwan" role="Founder & CEO" bio="Former AWS engineer with deep expertise in cloud infrastructure, security architecture, and building platforms at massive scale." linkedin="https://www.linkedin.com/in/mkansoh/" />
          <PersonCard img="images/Ivan.png" imgStyle={{ transform: "scale(1.5) translate(0, 8%)" }} name="Ivan Belfinger" role="Co-Founder & CTO" bio="Ex-Uber and Careem engineer with extensive experience in distributed systems, security engineering, and resilient infrastructure at global scale." linkedin="https://www.linkedin.com/in/ivan-belfinger-91b66660/" />
        </div>
      </div>
    </section>
  </>);
}

function PersonCard({ initials, img, imgStyle, name, role, bio, linkedin }) {
  if (img) {
    return (
      <div className="module person-card" style={{ minHeight: 380, padding: 36, alignItems: "center", textAlign: "center" }}>
        <div style={{ width: 144, height: 144, borderRadius: "50%", overflow: "hidden", background: "var(--surface-3)", border: "1px solid color-mix(in oklab, var(--teal) 30%, var(--border-2))", margin: "0 auto 22px" }}>
          <img src={img} alt={name} style={{ width: "100%", height: "100%", objectFit: "cover", ...imgStyle }} />
        </div>
        <div style={{ fontSize: 20, color: "var(--text)", fontWeight: 600, letterSpacing: "-0.005em" }}>{name}</div>
        <div style={{ fontSize: 13, color: "var(--teal)", fontWeight: 500, marginTop: 4, marginBottom: 14 }}>{role}</div>
        <p className="mod-desc" style={{ color: "var(--text-2)", fontSize: 13.5, lineHeight: 1.6, maxWidth: "42ch", margin: "0 auto 22px" }}>{bio}</p>
        {linkedin && (
          <a href={linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${name} on LinkedIn`}
            style={{ width: 36, height: 36, borderRadius: 8, display: "inline-grid", placeItems: "center", background: "var(--surface-3)", border: "1px solid var(--border-2)", color: "var(--text-2)", textDecoration: "none", margin: "0 auto", transition: "color .15s, border-color .15s" }}
            onMouseOver={(e) => { e.currentTarget.style.color = "var(--teal)"; e.currentTarget.style.borderColor = "color-mix(in oklab, var(--teal) 35%, var(--border-2))"; }}
            onMouseOut={(e) => { e.currentTarget.style.color = "var(--text-2)"; e.currentTarget.style.borderColor = "var(--border-2)"; }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
        )}
      </div>
    );
  }
  return (
    <div className="module" style={{ minHeight: 260 }}>
      <div className="mod-head" style={{ alignItems: "center" }}>
        <div style={{ width: 42, height: 42, borderRadius: "50%", display: "grid", placeItems: "center", background: "var(--surface-3)", border: "1px solid var(--border-2)", fontFamily: "var(--mono)", color: "var(--text-2)", fontSize: 13 }}>{initials}</div>
        <div>
          <div style={{ fontSize: 14, color: "var(--text)", fontWeight: 600 }}>{name}</div>
          <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>{role}</div>
        </div>
      </div>
      <p className="mod-desc" style={{ marginTop: 12 }}>{bio}</p>
    </div>
  );
}

/* ─── Careers ─── */

const CAREERS_API_ENDPOINT = "https://7cjtuz49li.execute-api.us-east-1.amazonaws.com/careers";

const CAREERS_ROLES = [
  {
    id: "pm",
    team: "Product",
    title: "Product Manager",
    type: "Full-time",
    loc: "Remote",
    blurb: "Own the roadmap for AtmosGuard's cloud security platform. Define what we build next across threat detection, compliance, and posture management.",
    sections: [
      { h: "About the role", p: "We're looking for a Product Manager to own the roadmap for AtmosGuard's cloud security platform. You'll work at the intersection of security, cloud infrastructure, and user experience, defining what we build across threat detection, compliance automation, and posture management." },
      {
        h: "What you'll do", list: [
          "Own the product roadmap for AtmosGuard's core platform, threat management, compliance, and CSPM.",
          "Translate customer pain points and security domain knowledge into clear product requirements.",
          "Drive prioritization across attack-path correlation, evidence pack generation, and risk scoring features.",
          "Work closely with engineering to ship features that reduce cloud risk for security teams.",
          "Define success metrics for detection fidelity, noise reduction, and audit readiness workflows.",
          "Conduct customer discovery with CISOs, security engineers, and compliance leads.",
          "Shape the go-to-market strategy for upcoming modules including SOAR, API security, and AI governance.",
        ]
      },
      {
        h: "What you bring", list: [
          "4+ years of product management experience, ideally in security, cloud infrastructure, or developer tools.",
          "Understanding of cloud security concepts, CSPM, SIEM, SOC2, ISO 27001, PCI-DSS, HIPAA.",
          "Experience working with AWS or Azure environments and cloud-native architectures.",
          "Strong technical communication, comfortable with engineers on detection logic and CISOs on risk posture.",
          "Track record shipping B2B SaaS products from zero-to-one or early-stage growth.",
          "Comfort working async in a fully remote, fast-moving team.",
        ]
      },
      {
        h: "Nice to have", list: [
          "Prior experience at a cloud security vendor (Wiz, Orca, Lacework, Prisma Cloud, etc.).",
          "Hands-on experience with Terraform, CloudFormation, or IaC tooling.",
          "Background in incident response or security operations.",
        ]
      },
    ],
  },
  {
    id: "be",
    team: "Engineering",
    title: "Backend Engineer",
    type: "Full-time",
    loc: "Remote",
    blurb: "Build the core detection engine, data pipelines, and cloud integrations that power AtmosGuard's threat correlation and risk prioritization.",
    sections: [
      { h: "About the role", p: "We're looking for a Backend Engineer to build the core systems that power AtmosGuard, from real-time log ingestion and threat correlation to risk scoring and compliance evidence generation. You'll work on distributed data pipelines that process cloud telemetry from AWS and Azure at scale." },
      {
        h: "What you'll do", list: [
          "Design and build the detection engine that correlates identity, network, and container signals into unified incidents.",
          "Build and optimize real-time data pipelines that ingest cloud logs (CloudTrail, VPC Flow Logs, Azure Activity Logs).",
          "Implement the risk scoring engine that prioritizes threats by likelihood, blast radius, and data classification.",
          "Build cloud integrations using read-only IAM roles, Terraform modules, and CloudFormation templates.",
          "Develop compliance control mapping and evidence pack generation for SOC2, HIPAA, PCI-DSS, ISO 27001, and NIST.",
          "Build APIs that power the AtmosGuard dashboard, multi-account visibility, posture findings, and incident investigation.",
          "Contribute to upcoming platform modules: SOAR playbook execution, API security runtime protection, AI governance auditing.",
        ]
      },
      {
        h: "What you bring", list: [
          "4+ years of backend engineering experience with Go, Python, or both.",
          "Deep experience with AWS, Lambda, ECS/EKS, S3, RDS, SQS, Kinesis, or equivalent.",
          "Experience building distributed systems handling high-throughput event processing.",
          "Familiarity with cloud security primitives, IAM, security groups, KMS, CloudTrail, GuardDuty.",
          "Strong understanding of API design, event-driven architectures, and infrastructure as code.",
          "Comfort working async in a fully remote team with high autonomy.",
        ]
      },
      {
        h: "Nice to have", list: [
          "Experience at a cloud security or observability company.",
          "Familiarity with threat detection frameworks (MITRE ATT&CK) and behavioral analytics.",
          "Experience with Azure cloud services and multi-cloud architectures.",
          "Background building SIEM, SOAR, or compliance automation systems.",
        ]
      },
    ],
  },
];

const GENERAL_ROLE = {
  id: "general",
  team: "General",
  title: "General Application",
  type: "Open",
  loc: "Remote",
  blurb: "Don't see your role? Tell us what you'd bring to the team, we're always interested in hearing from exceptional people.",
  sections: [
    { h: "About AtmosGuard", p: "AtmosGuard is an AI-native cloud security platform that surfaces the risks that actually matter. We unify threat detection, posture management, and compliance monitoring, built by ex-AWS and Uber engineers who've carried the pager." },
    { h: "Who we're looking for", p: "We don't have a specific role open that matches your background, but we're always interested in hearing from exceptional people. If you're passionate about cloud security, distributed systems, product design, or go-to-market strategy, tell us what you'd bring to the team." },
    {
      h: "What to include", list: [
        "Your CV or resume.",
        "A short note on why AtmosGuard interests you and what kind of role you're looking for.",
        "Links to any relevant work (GitHub, portfolio, blog, etc.).",
      ]
    },
  ],
};

function CareersPage() {
  const [activeRole, setActiveRole] = React.useState(null);

  // Lock body scroll while modal is open + close on Esc.
  React.useEffect(() => {
    if (!activeRole) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setActiveRole(null); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [activeRole]);

  // Card style, drop-in for .module without its 440px min-height baggage.
  const cardStyle = {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "20px 22px",
    transition: "border-color .2s ease, background .2s ease",
  };

  const VALUES = [
    { n: "01", h: "Own the outcome", p: "We don't hand off problems, we follow through until they're solved. Every person here is empowered to make decisions and accountable for the results." },
    { n: "02", h: "Default to transparency", p: "Information flows freely. We share context openly, document decisions publicly, and trust the team with the full picture." },
    { n: "03", h: "Move with conviction", p: "Speed matters, but so does direction. We make bold bets, ship fast, and iterate in the open. We'd rather course-correct than deliberate forever." },
    { n: "04", h: "Earn trust every day", p: "Trust isn't given by title, it's built through reliability, honesty, and follow-through. Teammates, customers, their data, all treated with respect." },
  ];

  const SPECS = [
    { k: "model", v: "hybrid · remote-friendly", c: "Dubai HQ for those who want desk + colleagues; fully async EMEA for everyone else." },
    { k: "timezone", v: "UTC+4 · core overlap 4h", c: "Pick your hours. We hold a four-hour overlap window so unblock-loops stay short." },
    { k: "comms", v: "written-first", c: "Decisions live in docs, not in calendars. Recurring meetings under 2h/week, by design." },
  ];

  const allRoles = [...CAREERS_ROLES, GENERAL_ROLE];

  return (<>
    <PageHeader
      kicker="§ CAREERS"
      title={<>Build the console<br /><span style={{ color: "var(--teal)" }}>your old SOC wished for.</span></>}
      lead="A small team, real customers, no theatre. We're hiring across engineering and product, and we read every general application that comes in."
      meta="REMOTE-FRIENDLY · GLOBAL"
      strip={[
        { k: "Open roles", v: String(CAREERS_ROLES.length).padStart(2, "0"), s: "engineering · product" },
        { k: "Working model", v: "Hybrid", s: "remote-friendly · EMEA" },
        { k: "Team size", v: "07", s: "growing to ~18 by EOY" },
        { k: "Median tenure", v: "all of us", s: "since seed · zero exits" },
      ]}
    />

    {/* OPEN ROLES */}
    <section className="section">
      <div className="wrap-wide">
        <div className="rail-title">
          <span className="micro">§ OPEN ROLES</span>
          <span className="mono" style={{ marginLeft: "auto", color: "var(--text-4)", fontSize: 11 }}>
            {String(CAREERS_ROLES.length).padStart(2, "0")} POSITIONS · ALL REMOTE · {new Date().toISOString().slice(0, 10)}
          </span>
        </div>
        <div className="changelog" style={{ padding: "4px 0", marginTop: 14 }}>
          {allRoles.map((r, i) => {
            const isGeneral = r.id === "general";
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setActiveRole(r)}
                className="row"
                style={{
                  textDecoration: "none",
                  padding: "20px 22px",
                  gridTemplateColumns: "56px 100px minmax(0,1fr) 140px 120px 24px",
                  alignItems: "center",
                  gap: 18,
                  background: "transparent",
                  border: 0,
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  color: "inherit",
                  font: "inherit",
                  borderBottom: i < allRoles.length - 1 ? "1px solid var(--border)" : "none",
                  opacity: isGeneral ? 0.92 : 1,
                }}
              >
                <span className="mono" style={{ color: isGeneral ? "var(--text-4)" : "var(--teal)", fontSize: 11, letterSpacing: "0.06em" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className={"pill " + (isGeneral ? "muted" : "teal")} style={{ justifySelf: "start" }}>
                  {r.team.toUpperCase()}
                </span>
                <span style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
                  <span style={{ color: "var(--text)", fontSize: 15, fontWeight: 500, letterSpacing: "-0.005em" }}>{r.title}</span>
                  <span style={{ color: "var(--text-3)", fontSize: 12.5, lineHeight: 1.5, paddingRight: 24 }}>{r.blurb}</span>
                </span>
                <span className="mono" style={{ color: "var(--text-3)", fontSize: 12 }}>{r.loc}</span>
                <span className="mono" style={{ color: "var(--text-3)", fontSize: 12 }}>{r.type}</span>
                <span style={{ color: isGeneral ? "var(--text-3)" : "var(--teal)", textAlign: "right", fontSize: 14 }}>→</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>

    {/* HOW WE WORK, config-spec table */}
    <section className="section" style={{ borderTop: "1px solid var(--border)" }}>
      <div className="wrap-wide">
        <div className="rail-title">
          <span className="micro">§ HOW WE WORK</span>
          <span className="mono" style={{ marginLeft: "auto", color: "var(--text-4)", fontSize: 11 }}>team.config · {SPECS.length} keys</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.6fr)", gap: 48, marginTop: 18, alignItems: "start" }}>
          <div>
            <h2 style={{ fontSize: 30, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
              Hybrid by default,<br />
              <span style={{ color: "var(--teal)" }}>remote-friendly always.</span>
            </h2>
            <p style={{ color: "var(--text-2)", fontSize: 14.5, lineHeight: 1.6, marginTop: 14 }}>
              We're built around a Dubai HQ with a global async backbone. The best work isn't tied to one city or one timezone, by hiring globally we get sharper perspectives, and the security thinking that comes with them.
            </p>
            <p style={{ color: "var(--text-3)", fontSize: 13.5, lineHeight: 1.6, marginTop: 10 }}>
              You choose where you work; we measure output, not hours logged.
            </p>
          </div>
          <div style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            overflow: "hidden",
          }}>
            {SPECS.map((s, i) => (
              <div key={s.k} style={{
                display: "grid",
                gridTemplateColumns: "140px minmax(0, 1fr)",
                gap: 24,
                padding: "16px 22px",
                borderBottom: i < SPECS.length - 1 ? "1px solid var(--border)" : "none",
                alignItems: "baseline",
              }}>
                <span className="mono" style={{ color: "var(--text-4)", fontSize: 11.5, letterSpacing: "0.06em" }}>{s.k}</span>
                <div>
                  <div className="mono" style={{ color: "var(--teal)", fontSize: 13, letterSpacing: "-0.005em" }}>{s.v}</div>
                  <div style={{ color: "var(--text-3)", fontSize: 12.5, lineHeight: 1.55, marginTop: 5 }}>{s.c}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* VALUES, flat 4-up grid */}
    <section className="section" style={{ borderTop: "1px solid var(--border)" }}>
      <div className="wrap-wide">
        <div className="rail-title">
          <span className="micro">§ LEADERSHIP VALUES</span>
          <span className="mono" style={{ marginLeft: "auto", color: "var(--text-4)", fontSize: 11 }}>04 PRINCIPLES · NOT POSTERS</span>
        </div>
        <p style={{ color: "var(--text-2)", fontSize: 15, marginTop: 14, maxWidth: "60ch", lineHeight: 1.55 }}>
          The principles we use to make decisions, give feedback, and hold each other accountable. They're load-bearing, not slogans.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14, marginTop: 24 }}>
          {VALUES.map((v) => (
            <div key={v.n} style={cardStyle}>
              <span className="mono" style={{ fontSize: 11.5, color: "var(--teal)", letterSpacing: "0.08em" }}>{v.n}</span>
              <h3 style={{ fontSize: 17, letterSpacing: "-0.012em", marginTop: 10, lineHeight: 1.25 }}>{v.h}</h3>
              <p style={{ color: "var(--text-3)", fontSize: 13, lineHeight: 1.6, marginTop: 10 }}>{v.p}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Don't see a role, closes the page */}
    <section className="section" style={{ borderTop: "1px solid var(--border)" }}>
      <div className="wrap-wide">
        <div style={{
          padding: "20px 24px",
          border: "1px dashed var(--border-2)", borderRadius: 10,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 16, flexWrap: "wrap",
        }}>
          <div>
            <div style={{ color: "var(--text-2)", fontSize: 14 }}>Don't see a role that fits?</div>
            <div style={{ color: "var(--text-4)", fontSize: 12, marginTop: 4 }}>
              We're always looking for exceptional people across engineering, product, and go-to-market.
            </div>
          </div>
          <button type="button" className="btn" onClick={() => setActiveRole(GENERAL_ROLE)}>Reach out anyway →</button>
        </div>
      </div>
    </section>

    {activeRole ? <JobModal role={activeRole} onClose={() => setActiveRole(null)} /> : null}
  </>);
}

function JobModal({ role, onClose }) {
  const [view, setView] = React.useState("jd"); // "jd" | "apply"
  const [status, setStatus] = React.useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = React.useState("");
  const [cvFile, setCvFile] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const formRef = React.useRef(null);

  function onCvChange(file) {
    if (!file) { setCvFile(null); return; }
    if (file.size > 5 * 1024 * 1024) {
      setErrorMsg("CV must be under 5MB.");
      setStatus("error");
      return;
    }
    const allowed = [".pdf", ".doc", ".docx"];
    const lower = file.name.toLowerCase();
    if (!allowed.some(ext => lower.endsWith(ext))) {
      setErrorMsg("CV must be PDF, DOC, or DOCX.");
      setStatus("error");
      return;
    }
    setCvFile(file);
    if (status === "error") { setStatus("idle"); setErrorMsg(""); }
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!cvFile) {
      setErrorMsg("Please attach your CV.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");

    const fd = new FormData(formRef.current);
    fd.set("role", role.title);
    fd.set("cv", cvFile, cvFile.name);

    try {
      const res = await fetch(CAREERS_API_ENDPOINT, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setStatus("success");
      } else {
        setErrorMsg(data.error || "Submission failed. Please try again.");
        setStatus("error");
      }
    } catch (err) {
      setErrorMsg("Network error. Please try again.");
      setStatus("error");
    }
  }

  const submitting = status === "submitting";

  return (
    <div role="dialog" aria-modal="true" aria-label={role.title} style={{
      position: "fixed", inset: 0, zIndex: 100,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: "40px 16px",
      overflowY: "auto",
    }}>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0,
        background: "rgba(5,9,12,0.78)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }} />
      <div style={{
        position: "relative",
        width: "100%", maxWidth: 680,
        background: "var(--surface)",
        border: "1px solid var(--border-2)",
        borderRadius: 14,
        boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(56,189,248,0.04)",
        overflow: "hidden",
        margin: "auto",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 18px",
          borderBottom: "1px solid var(--border)",
          background: "linear-gradient(180deg, rgba(56,189,248,0.04) 0%, transparent 100%)",
        }}>
          <span className="micro" style={{ color: "var(--teal)", letterSpacing: "0.12em", fontSize: 10.5, fontWeight: 600 }}>§ {role.team.toUpperCase()}</span>
          <span style={{ color: "var(--text-4)" }}>·</span>
          <span className="mono" style={{ color: "var(--text-3)", fontSize: 11 }}>{role.type.toUpperCase()} · {role.loc.toUpperCase()}</span>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            marginLeft: "auto",
            background: "transparent", border: "1px solid var(--border)", color: "var(--text-3)",
            width: 28, height: 28, borderRadius: 6,
            display: "grid", placeItems: "center", cursor: "pointer",
            fontSize: 14, lineHeight: 1,
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto" }}>
          {view === "jd" ? (
            <div style={{ padding: "24px 28px 28px" }}>
              <h2 style={{ fontSize: 28, letterSpacing: "-0.02em", lineHeight: 1.15 }}>{role.title}</h2>
              <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                <span className="pill teal">{role.team.toUpperCase()}</span>
                <span className="pill muted">{role.type}</span>
                <span className="pill muted">{role.loc}</span>
              </div>

              <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 22 }}>
                {role.sections.map((s, i) => (
                  <div key={i}>
                    <div className="rail-title" style={{ marginBottom: 10 }}>
                      <span className="micro">§ {s.h.toUpperCase()}</span>
                    </div>
                    {s.p ? (
                      <p style={{ color: "var(--text-2)", fontSize: 14, lineHeight: 1.6 }}>{s.p}</p>
                    ) : null}
                    {s.list ? (
                      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
                        {s.list.map((li, j) => (
                          <li key={j} style={{ display: "grid", gridTemplateColumns: "14px 1fr", gap: 10, alignItems: "start", color: "var(--text-2)", fontSize: 13.5, lineHeight: 1.55 }}>
                            <span className="mono" style={{ color: "var(--teal)", fontSize: 11, paddingTop: 3 }}>{String(j + 1).padStart(2, "0")}</span>
                            <span>{li}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 28, paddingTop: 20, borderTop: "1px solid var(--border)", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <button type="button" className="btn primary lg" onClick={() => setView("apply")}>
                  Apply for this role →
                </button>
                <span className="mono" style={{ color: "var(--text-4)", fontSize: 11, marginLeft: 6 }}>cv goes to careers@atmosguard.io · 2-min form</span>
              </div>
            </div>
          ) : status === "success" ? (
            <div style={{ padding: "56px 28px", textAlign: "center" }}>
              <div style={{
                width: 48, height: 48, margin: "0 auto",
                borderRadius: "50%",
                background: "color-mix(in oklab, var(--teal) 14%, transparent)",
                border: "1px solid color-mix(in oklab, var(--teal) 40%, transparent)",
                display: "grid", placeItems: "center",
                color: "var(--teal)", fontSize: 22,
              }}>✓</div>
              <h2 style={{ fontSize: 24, letterSpacing: "-0.018em", marginTop: 18 }}>Application received.</h2>
              <p style={{ color: "var(--text-2)", marginTop: 10, fontSize: 14, maxWidth: "42ch", margin: "10px auto 0" }}>
                Thanks for applying for <strong style={{ color: "var(--text)" }}>{role.title}</strong>. We read every application, expect a note from us within 5 business days.
              </p>
              <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "center" }}>
                <button type="button" className="btn" onClick={onClose}>Close</button>
              </div>
            </div>
          ) : (
            <form ref={formRef} onSubmit={onSubmit} style={{ padding: "24px 28px 28px" }} noValidate>
              <button type="button" onClick={() => setView("jd")} style={{
                background: "transparent", border: 0, color: "var(--text-3)",
                fontSize: 12, cursor: "pointer", padding: 0, marginBottom: 14,
                display: "inline-flex", alignItems: "center", gap: 6,
              }}>← Back to job description</button>
              <h2 style={{ fontSize: 24, letterSpacing: "-0.02em" }}>Apply</h2>
              <p className="mono" style={{ color: "var(--text-3)", fontSize: 11.5, marginTop: 6 }}>APPLYING FOR · <span style={{ color: "var(--text-2)" }}>{role.title.toUpperCase()}</span></p>

              {/* Honeypot */}
              <input type="text" name="website" tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} aria-hidden="true" />

              <div style={{ marginTop: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="First name" name="firstName" required />
                <Field label="Last name" name="lastName" required />
              </div>
              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Email" name="email" type="email" required />
                <Field label="Phone" name="phone" type="tel" required />
              </div>
              <div style={{ marginTop: 12 }}>
                <Field label="LinkedIn" name="linkedin" type="url" placeholder="https://linkedin.com/in/…" />
              </div>

              {/* CV dropzone */}
              <div style={{ marginTop: 14 }}>
                <label className="mono" style={{ display: "block", color: "var(--text-3)", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Resume / CV <span style={{ color: "var(--red)" }}>*</span></label>
                <label
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const f = e.dataTransfer.files && e.dataTransfer.files[0];
                    if (f) onCvChange(f);
                  }}
                  style={{
                    display: "block",
                    border: "1px dashed " + (dragOver ? "var(--teal)" : "var(--border-2)"),
                    background: dragOver ? "color-mix(in oklab, var(--teal) 6%, transparent)" : "var(--bg-2)",
                    borderRadius: 8,
                    padding: cvFile ? "14px 16px" : "26px 16px",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "border-color .15s, background .15s",
                  }}
                >
                  <input
                    type="file"
                    name="cv"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => onCvChange(e.target.files[0])}
                    style={{ position: "absolute", left: "-9999px" }}
                    aria-label="Resume or CV"
                  />
                  {cvFile ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, fontSize: 13 }}>
                      <span className="mono pill muted">CV</span>
                      <span style={{ color: "var(--text)" }}>{cvFile.name}</span>
                      <span className="mono" style={{ color: "var(--text-4)", fontSize: 11 }}>{(cvFile.size / 1024).toFixed(0)} KB</span>
                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCvFile(null); }} style={{ background: "transparent", border: 0, color: "var(--text-3)", cursor: "pointer", marginLeft: 6, fontSize: 14 }}>×</button>
                    </div>
                  ) : (
                    <>
                      <div style={{ fontSize: 13.5, color: "var(--text-2)" }}>Drop your CV here, or <span style={{ color: "var(--teal)" }}>browse</span></div>
                      <div className="mono" style={{ fontSize: 11, color: "var(--text-4)", marginTop: 6 }}>PDF · DOC · DOCX · MAX 5MB</div>
                    </>
                  )}
                </label>
              </div>

              <div style={{ marginTop: 14 }}>
                <label className="mono" style={{ display: "block", color: "var(--text-3)", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>
                  Why AtmosGuard? <span style={{ color: "var(--text-4)", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
                </label>
                <textarea
                  name="coverNote"
                  rows={4}
                  placeholder="Tell us why you're interested in this role…"
                  style={fieldStyle()}
                />
              </div>

              {status === "error" ? (
                <div style={{
                  marginTop: 14, padding: "10px 12px",
                  border: "1px solid color-mix(in oklab, var(--red) 35%, var(--border))",
                  background: "color-mix(in oklab, var(--red) 8%, transparent)",
                  borderRadius: 6, color: "var(--red)", fontSize: 12.5,
                }}>{errorMsg || "Something went wrong."}</div>
              ) : null}

              <div style={{ marginTop: 18, display: "flex", gap: 10, alignItems: "center" }}>
                <button type="submit" className="btn primary lg" disabled={submitting} style={submitting ? { opacity: 0.7, cursor: "wait" } : null}>
                  {submitting ? "Submitting…" : "Submit application →"}
                </button>
                <span className="mono" style={{ color: "var(--text-4)", fontSize: 11 }}>secure upload · cv stored in s3</span>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function fieldStyle() {
  return {
    width: "100%",
    background: "var(--bg-2)",
    border: "1px solid var(--border)",
    borderRadius: 6,
    color: "var(--text)",
    fontFamily: "var(--sans)",
    fontSize: 13.5,
    padding: "10px 12px",
    outline: "none",
    transition: "border-color .15s, box-shadow .15s",
    resize: "vertical",
  };
}

function Field({ label, name, type = "text", required, placeholder, value, onChange }) {
  const [focus, setFocus] = React.useState(false);
  return (
    <label style={{ display: "block" }}>
      <span className="mono" style={{ display: "block", color: "var(--text-3)", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
        {label} {required ? <span style={{ color: "var(--red)" }}>*</span> : null}
      </span>
      <input
        type={type}
        name={name}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          ...fieldStyle(),
          borderColor: focus ? "var(--teal)" : "var(--border)",
          boxShadow: focus ? "0 0 0 3px var(--teal-glow)" : "none",
        }}
      />
    </label>
  );
}

/* ─── Contact page ─── */

function ContactPage() {
  const [status, setStatus] = React.useState('idle');
  const [errorMsg, setErrorMsg] = React.useState('');
  const waitlistParams = React.useMemo(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get('topic') !== 'waitlist') return null;
    const id = q.get('product') || '';
    const hit = PRODUCTS.find((x) => x.id === id);
    return { message: 'Waitlist: ' + (hit ? hit.name : id) };
  }, []);
  const [topic, setTopic] = React.useState(waitlistParams ? 'waitlist' : 'sales');
  const formRef = React.useRef(null);

  async function onSubmit(e) {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting'); setErrorMsg('');
    const fd = new FormData(e.target);
    const data = {
      source: 'contact-page',
      name: fd.get('name'),
      email: fd.get('email'),
      company: fd.get('company'),
      role: fd.get('role'),
      topic: fd.get('topic'),
      message: fd.get('message'),
      website: fd.get('website'),
    };
    try {
      const res = await fetch(DEMO_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const result = await res.json();
      if (result && result.ok) setStatus('success');
      else throw new Error((result && result.error) || 'Submission failed');
    } catch (err) {
      setErrorMsg(err.message || 'Network error — please try again.');
      setStatus('error');
    }
  }

  const submitting = status === 'submitting';

  const TOPICS = [
    { id: 'sales', label: 'Sales · demo' },
    { id: 'waitlist', label: 'Waitlist' },
    { id: 'product', label: 'Product question' },
    { id: 'compliance', label: 'Compliance · audit' },
    { id: 'partnership', label: 'Partnership' },
    { id: 'press', label: 'Press' },
    { id: 'other', label: 'Other' },
  ];

  return (<>
    <PageHeader
      kicker="§ CONTACT"
      title={<>One inbox,<br /><span style={{ color: "var(--teal)" }}>an engineer at the other end.</span></>}
      lead="Tell us what you're trying to solve. A founder or engineer reads every message and writes back the same day. No qualification gauntlets, no SDR loops, no canned auto-replies past the first acknowledgement."
      meta="DUBAI MEDIA CITY · UAE"
      strip={[
        { k: "Headquarters", v: "Dubai", s: "Media City · UAE" },
        { k: "Response", v: "< 1 day", s: "every business day" },
        { k: "Office hours", v: "09–18 GST", s: "UTC+4 · Mon–Fri" },
        { k: "General", v: "info@", s: "atmosguard.io" },
      ]}
    />

    <section className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 01 — SEND A MESSAGE</span></div>
            <h2>Write us a line.</h2>
            <p style={{ marginTop: 12 }}>
              Whether you want a live evaluation, have a compliance question,
              are exploring a partnership, or just have feedback on something we
              shipped, it lands in the same inbox and gets read the same day.
            </p>
          </div>
          <div className="micro" style={{ color: "var(--text-3)" }}>REPLY WITHIN 1 BUSINESS DAY · NO SDR LOOP</div>
        </div>

        <div className="contact-grid">
          <div className="contact-form-card">
            <div className="contact-form-head">
              <span className="dot live" />
              <span className="mono" style={{ color: "var(--text-2)", fontSize: 12 }}>atmosguard / contact / new-message</span>
              <span className="kbd mono" style={{ marginLeft: "auto" }}>↵ to send</span>
            </div>

            {status === 'success' ? (
              <div style={{ padding: "56px 28px", textAlign: "center" }}>
                <div style={{
                  width: 48, height: 48, margin: "0 auto", borderRadius: "50%",
                  background: "color-mix(in oklab, var(--teal) 14%, transparent)",
                  border: "1px solid color-mix(in oklab, var(--teal) 40%, transparent)",
                  display: "grid", placeItems: "center",
                  color: "var(--teal)", fontSize: 22,
                }}>✓</div>
                <h3 style={{ fontSize: 22, letterSpacing: "-0.018em", marginTop: 18 }}>Message received.</h3>
                <p style={{ color: "var(--text-2)", marginTop: 10, fontSize: 14, maxWidth: "42ch", margin: "10px auto 0" }}>
                  Logged as <span className="mono" style={{ color: "var(--teal)" }}>msg-{Math.random().toString(36).slice(2, 8).toUpperCase()}</span>.
                  A founder or engineer will reach out within one business day.
                </p>
                <div style={{ marginTop: 24 }}>
                  <button type="button" className="btn" onClick={() => { setStatus('idle'); setErrorMsg(''); }}>Send another</button>
                </div>
              </div>
            ) : (
              <form ref={formRef} onSubmit={onSubmit} className="contact-form" noValidate>
                <input type="text" name="website" tabIndex={-1} autoComplete="off"
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} aria-hidden="true" />

                <div className="cf-row cf-row-2">
                  <Field label="Full Name" name="name" required />
                  <Field label="Work Email" name="email" type="email" required placeholder="you@company.com" />
                </div>
                <div className="cf-row cf-row-2">
                  <Field label="Company" name="company" required />
                  <Field label="Role" name="role" placeholder="e.g. CISO, Platform Eng" />
                </div>

                <div className="cf-row">
                  <label style={{ display: "block" }}>
                    <span className="cf-label mono">Topic</span>
                    <input type="hidden" name="topic" value={topic} />
                    <div className="cf-pills">
                      {TOPICS.map(t => (
                        <button key={t.id} type="button"
                          onClick={() => setTopic(t.id)}
                          className={"pill " + (topic === t.id ? "teal" : "muted")}
                          style={{ cursor: "pointer", border: "1px solid", borderColor: topic === t.id ? "color-mix(in oklab, var(--teal) 35%, transparent)" : "var(--border)" }}>
                          {topic === t.id ? "✓ " : ""}{t.label}
                        </button>
                      ))}
                    </div>
                  </label>
                </div>

                <div className="cf-row">
                  <label style={{ display: "block" }}>
                    <span className="cf-label mono">Message <span style={{ color: "var(--red)" }}>*</span></span>
                    <textarea
                      name="message"
                      defaultValue={waitlistParams ? waitlistParams.message : undefined}
                      required
                      rows={6}
                      placeholder="What are you trying to solve? Which clouds, roughly how many accounts, and any compliance frameworks in scope."
                      style={fieldStyle()}
                    />
                  </label>
                </div>

                {status === 'error' ? (
                  <div className="cf-error">{errorMsg || "Something went wrong."}</div>
                ) : null}

                <div className="cf-actions">
                  <button type="submit" className="btn primary lg" disabled={submitting}
                    style={submitting ? { opacity: 0.7, cursor: "wait" } : null}>
                    {submitting ? "Sending…" : "Send message"}
                    {!submitting && <span className="kbd mono">↵</span>}
                  </button>
                  <span className="mono" style={{ color: "var(--text-4)", fontSize: 11 }}>
                    or email <a href="mailto:info@atmosguard.io" style={{ color: "var(--text-2)" }}>info@atmosguard.io</a> · we never sell or share contact details
                  </span>
                </div>
              </form>
            )}
          </div>

          <aside className="contact-side">
            <div className="contact-side-block">
              <span className="micro" style={{ color: "var(--text-4)" }}>§ HEADQUARTERS</span>
              <div className="cs-h">Dubai Media City</div>
              <div className="cs-sub">Dubai · United Arab Emirates</div>
              <div className="cs-meta mono">25.0945° N · 55.1539° E · GMT+4</div>
            </div>

            <div className="contact-side-block">
              <span className="micro" style={{ color: "var(--text-4)" }}>§ OFFICE HOURS</span>
              <div className="cs-hours">
                <div className="cs-hours-row">
                  <span className="mono">Mon — Fri</span>
                  <span className="mono cs-v">09:00 — 18:00 GST</span>
                </div>
                <div className="cs-hours-row dim">
                  <span className="mono">Sat — Sun</span>
                  <span className="mono">closed · monitored</span>
                </div>
              </div>
              <div className="cs-meta mono" style={{ marginTop: 10 }}>
                urgent? prefix the subject with [P0]. Pages the on-call engineer.
              </div>
            </div>

            <div className="contact-side-block">
              <span className="micro" style={{ color: "var(--text-4)" }}>§ ELSEWHERE</span>
              <ul className="cs-list">
                <li>
                  <span className="cs-k mono">linkedin</span>
                  <a className="cs-v mono" target="_blank" rel="noopener noreferrer" href="https://www.linkedin.com/company/atmosguard/">atmosguard ↗</a>
                </li>
                <li>
                  <span className="cs-k mono">careers</span>
                  <a className="cs-v mono" href={L("careers.html")}>open roles →</a>
                </li>
                <li>
                  <span className="cs-k mono">trial</span>
                  <a className="cs-v mono" href="https://app.atmosguard.io/register">
                    start free trial →
                  </a>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 02 — LOCATION</span></div>
            <h2>Where the build happens.</h2>
            <p style={{ marginTop: 12 }}>
              We're in Dubai Media City — the tech and media cluster on the Gulf coast,
              twenty minutes from the airport, three hours from London, four from Mumbai.
              Drop in for a coffee if you're in town.
            </p>
          </div>
          <div className="micro" style={{ color: "var(--text-3)" }}>DUBAI MEDIA CITY · UAE · GMT+4</div>
        </div>

        <div className="contact-map">
          <iframe
            title="Map of Dubai Media City"
            className="contact-map-frame"
            src="https://www.openstreetmap.org/export/embed.html?bbox=55.1280%2C25.0780%2C55.1900%2C25.1130&amp;layer=mapnik"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="contact-map-grid" aria-hidden="true" />
          <div className="contact-map-vignette" aria-hidden="true" />

          <div className="contact-map-overlay">
            <span className="dot live" />
            <div>
              <div className="micro" style={{ color: "var(--text-4)" }}>§ ATMOSGUARD HQ</div>
              <div className="cm-h">Dubai Media City</div>
              <div className="cm-sub mono">Dubai · United Arab Emirates</div>
            </div>
          </div>

          <div className="contact-map-meta mono">
            <span>tile: openstreetmap</span>
            <span className="sep">·</span>
            <span>bbox: 55.128, 25.078 → 55.190, 25.113</span>
            <span className="sep">·</span>
            <span style={{ color: "var(--teal)" }}>view: high-level</span>
          </div>
        </div>
      </div>
    </section>
  </>);
}

/* ─── Blog ─── */

function BlogIndexPage({ posts }) {
  const all = (posts && posts.length) ? posts : ((typeof window !== "undefined" && window.__BLOG_INDEX__) || []);
  if (!all.length) {
    return (<>
      <PageHeader
        kicker="§ BLOG — FIELD NOTES"
        title={<>Notes from the<br /><span style={{ color: "var(--teal)" }}>quiet console.</span></>}
        lead="Engineering, detection, and compliance writing from the AtmosGuard team. No vendor takes; just what we shipped, what broke, and what we measured."
        meta="0 POSTS"
      />
      <section className="section">
        <div className="wrap-wide" style={{ padding: "40px 0 120px", color: "var(--text-3)", textAlign: "center" }}>
          <p>No posts yet.</p>
        </div>
      </section>
    </>);
  }
  const groups = {};
  all.forEach(p => { (groups[p.cat || "Uncategorized"] ||= []).push(p); });
  const preferred = ["Engineering", "Architecture", "Detection", "Compliance", "Changelog", "Release"];
  const groupOrder = [
    ...preferred.filter(c => groups[c] && groups[c].length),
    ...Object.keys(groups).filter(c => !preferred.includes(c)),
  ];
  return (<>
    <PageHeader
      kicker="§ BLOG — FIELD NOTES"
      title={<>Notes from the<br /><span style={{ color: "var(--teal)" }}>quiet console.</span></>}
      lead="Engineering, detection, and compliance writing from the AtmosGuard team, and the customers who put it through its paces. No vendor takes; just what we shipped, what broke, and what we measured."
      meta={`${all.length} POST${all.length === 1 ? "" : "S"} · UPDATED WEEKLY`}
    />
    <section className="section">
      <div className="wrap-wide">
        {groupOrder.map((cat, gi) => (
          <div key={cat} style={{ marginTop: gi === 0 ? 0 : 56 }}>
            <div className="rail-title">
              <span className="micro">§ {cat.toUpperCase()}</span>
              <span className="mono" style={{ marginLeft: "auto", color: "var(--text-4)", fontSize: 11 }}>
                {String(groups[cat].length).padStart(2, "0")} POST{groups[cat].length === 1 ? "" : "S"}
              </span>
            </div>
            <div className="blog-grid" style={{ marginTop: 18 }}>
              {groups[cat].map((p, i) => (
                <a key={i} href={L("blog/posts/" + p.slug + ".html")} className="blog-card">
                  <div className="blog-card-head">
                    <span className="pill muted">{p.cat}</span>
                    <span className="mono" style={{ color: "var(--text-4)", fontSize: 11, marginLeft: "auto" }}>{p.read}</span>
                  </div>
                  <h3 style={{ fontSize: 20, marginTop: 12, letterSpacing: "-0.012em" }}>{p.title}</h3>
                  <p style={{ marginTop: 8, color: "var(--text-3)", fontSize: 13.5, lineHeight: 1.55 }}>{p.dek}</p>
                  <div className="bc-mono">{p.date}{p.author ? " · " + p.author.split(" ")[0] : ""}</div>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  </>);
}

function initials(name) {
  return name.split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
}

function BlogPostPage({ post, allPosts }) {
  const p = post || (typeof window !== "undefined" ? window.__POST__ : null);
  if (!p) return <div className="wrap-wide" style={{ padding: "120px 0" }}><h1>Post not found.</h1></div>;
  const all = (allPosts && allPosts.length) ? allPosts : ((typeof window !== "undefined" && window.__BLOG_INDEX__) || []);
  const idx = all.findIndex(x => x.slug === p.slug);
  const next = (all.length > 1 && idx >= 0) ? all[(idx + 1) % all.length] : null;
  return (<>
    <article className="blog-article">
      <div className="wrap" style={{ maxWidth: 760 }}>
        <div className="rail-title" style={{ marginTop: 56 }}><span className="micro">§ {(p.cat || "").toUpperCase()}{p.cat && p.date ? " · " : ""}{p.date}</span></div>
        <h1 style={{ fontSize: 52, lineHeight: 1.05, letterSpacing: "-0.024em", marginTop: 14 }}>{p.title}</h1>
        {p.dek && <p style={{ marginTop: 22, fontSize: 18, color: "var(--text-2)", lineHeight: 1.5 }}>{p.dek}</p>}
        <div className="blog-byline">
          <span className="avatar mono" style={{ width: 36, height: 36, borderRadius: "50%", display: "grid", placeItems: "center", background: "var(--surface-3)", border: "1px solid var(--border-2)", fontSize: 12, color: "var(--text-2)" }}>{initials(p.author || "")}</span>
          <div>
            <div style={{ fontSize: 14, color: "var(--text)" }}>{p.author}</div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>{p.read}</div>
          </div>
        </div>
        <div className="blog-prose" dangerouslySetInnerHTML={{ __html: p.body_html || "" }} />
        {next && (
          <div className="blog-next">
            <span className="micro">§ READ NEXT</span>
            <a href={L("blog/posts/" + next.slug + ".html")} className="blog-next-card">
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>{(next.cat || "").toUpperCase()}{next.cat && next.date ? " · " : ""}{next.date}</div>
              <div style={{ fontSize: 20, color: "var(--text)", marginTop: 8, letterSpacing: "-0.012em" }}>{next.title}</div>
              <span className="mono" style={{ color: "var(--teal)", marginTop: 14, display: "inline-block" }}>READ →</span>
            </a>
          </div>
        )}
      </div>
    </article>
  </>);
}

/* ─── Legal pages (privacy, terms, disclosure) ─── */

const LEGAL_EFFECTIVE = "2026-05-11";
const LEGAL_CONTACT = "legal@atmosguard.io";

function LegalPage({ kicker, title, lead, meta, strip, sections, contactLine }) {
  return (<>
    <PageHeader
      kicker={kicker}
      title={title}
      lead={lead}
      meta={meta}
      strip={strip}
    />
    <section className="section">
      <div className="wrap-wide">
        <div className="legal-wrap">
          {sections.map((s, i) => (
            <article key={i} id={s.id || undefined} className="legal-block">
              <div className="rail-title">
                <span className="micro">§ {String(i + 1).padStart(2, "0")} — {s.h.toUpperCase()}</span>
              </div>
              <h2 className="legal-h">{s.h}</h2>
              {s.body && s.body.map((p, j) => (
                <p key={j} className="legal-p">{p}</p>
              ))}
              {s.list && (
                <ul className="legal-list">
                  {s.list.map((it, k) => (
                    <li key={k}>{it}</li>
                  ))}
                </ul>
              )}
            </article>
          ))}
          {contactLine && (
            <article className="legal-block legal-contact">
              <div className="rail-title">
                <span className="micro">§ CONTACT</span>
              </div>
              <h2 className="legal-h">Get in touch.</h2>
              <p className="legal-p">{contactLine}</p>
            </article>
          )}
        </div>
      </div>
    </section>
  </>);
}

function PrivacyPage() {
  return (
    <LegalPage
      kicker="§ LEGAL — PRIVACY POLICY"
      title={<>How we handle the data<br /><span style={{ color: "var(--teal)" }}>you trust us with.</span></>}
      lead="AtmosGuard is a cloud security platform. We hold ourselves to the same data discipline we ask of our customers, minimum collection, clear purpose, signed evidence, and the right to leave with your data intact."
      meta={`EFFECTIVE ${LEGAL_EFFECTIVE} · DUBAI · UAE`}
      sections={[
        {
          h: "What this covers",
          body: [
            "This Privacy Policy describes how AtmosGuard, Inc. (\"AtmosGuard\", \"we\", \"us\") collects, uses, shares, and protects information when you use the AtmosGuard platform, our website at atmosguard.io, and related services (together, the \"Services\").",
            "We are the data controller for the personal information described here, except where AtmosGuard processes customer cloud telemetry on behalf of a customer, in which case we act as a data processor under the customer's instructions and the executed Data Processing Addendum.",
          ],
        },
        {
          h: "Information we collect",
          body: [
            "We collect three categories of information, each with a specific purpose:",
          ],
          list: [
            "Account information — name, business email, company, role, authentication metadata, billing details. Used to provision and operate your account.",
            "Cloud telemetry — read-only metadata pulled from connected AWS and Azure environments via the role you authorise, including resource configurations, identity events, control-plane activity, and tag metadata. Used to detect risk, correlate threats, and produce evidence.",
            "Product usage — pages viewed, features used, search queries inside the console, and diagnostic logs. Used to operate, secure, and improve the Services.",
          ],
        },
        {
          h: "How we use it",
          list: [
            "Detect, rank, and explain cloud risk for your environment.",
            "Generate compliance evidence and audit trails on your behalf.",
            "Operate, monitor, and secure the Services, including abuse and fraud prevention.",
            "Communicate about the Services, security advisories, and product changes.",
            "Comply with legal obligations and respond to lawful requests.",
          ],
        },
        {
          h: "What we do not do",
          list: [
            "We do not sell personal information. We never have.",
            "We do not use customer cloud telemetry to train models that serve other customers.",
            "We do not deploy advertising trackers or third-party analytics that profile you across the open web.",
            "We do not write to your cloud accounts. AtmosGuard's connector is read-only by design.",
          ],
        },
        {
          h: "How we share it",
          body: [
            "We share information only with sub-processors that help us operate the Services, with parties you direct us to (for example, your Slack or Microsoft Teams workspace), and where required by law.",
          ],
        },
        {
          h: "Where it lives",
          body: [
            "Customer telemetry and account data are processed in AWS US-EAST by default. EU data residency is available on request for Pro and Enterprise plans. All data is encrypted in transit (TLS 1.2+) and at rest (AES-256).",
            "Data retention is governed by your subscription tier. Each plan sets its own telemetry, evidence, and audit-log retention windows, and the limits that apply to your account are documented in your plan and in the console. On termination, we delete your data in line with the retention window of the tier you were on, with a signed deletion attestation on request.",
          ],
        },
        {
          h: "Your rights",
          body: [
            "Depending on where you live, you may have the right to access, correct, export, or delete your personal information, to object to or restrict certain processing, and to lodge a complaint with a supervisory authority.",
            "You can exercise these rights at any time by writing to info@atmosguard.io. We respond within 30 days. We will not retaliate against you for exercising any of these rights.",
          ],
        },
        {
          h: "Cookies",
          body: [
            "We use a small number of strictly necessary cookies to keep you signed in and to remember preferences (theme, density, dismissed banners). We do not use advertising or cross-site tracking cookies on atmosguard.io or in the product.",
          ],
        },
        {
          h: "Changes",
          body: [
            "If we make material changes to this policy we will notify you via the Services or by email at least 30 days before they take effect. The effective date at the top of this page reflects the current version.",
          ],
        },
      ]}
      contactLine={<>Questions about this policy, sub-processors, or a data request? Email <a href="mailto:info@atmosguard.io">info@atmosguard.io</a>.</>}
    />
  );
}

function TermsPage() {
  return (
    <LegalPage
      kicker="§ LEGAL — TERMS OF SERVICE"
      title={<>The rules of the road<br /><span style={{ color: "var(--teal)" }}>for using AtmosGuard.</span></>}
      lead="The terms below govern access to AtmosGuard's platform, website, and related services. They are written to be read, not just signed, plain language where the law allows it, lawyer language where it requires it."
      meta={`EFFECTIVE ${LEGAL_EFFECTIVE} · DUBAI · UAE`}
      sections={[
        {
          h: "Acceptance",
          body: [
            "These Terms of Service (the \"Terms\") form a binding agreement between you (or the entity you represent) and AtmosGuard, Inc. (\"AtmosGuard\", \"we\", \"us\"). By creating an account, accessing the platform, or otherwise using the Services, you accept these Terms.",
            "If you accept on behalf of an organisation, you represent that you have authority to bind that organisation. Where you have signed a separate Master Services Agreement with AtmosGuard, that MSA controls and these Terms supplement it.",
          ],
        },
        {
          h: "The Services",
          body: [
            "AtmosGuard provides a cloud security platform that ingests read-only telemetry from your connected AWS and Azure environments, ranks exposures by risk, correlates threats, and produces compliance evidence. The features available to you depend on your plan and any add-ons you have enabled.",
            "We may improve, change, or discontinue features over time. Where a change materially reduces functionality you rely on, we will give you at least 30 days' notice and, where reasonable, a path to comparable functionality.",
          ],
        },
        {
          h: "Your account",
          list: [
            "You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account.",
            "You must use strong authentication and enable SSO and MFA where your plan supports it.",
            "You will notify us promptly at info@atmosguard.io if you suspect unauthorised access.",
            "You are responsible for ensuring that the cloud roles you grant AtmosGuard are read-only and scoped to the accounts you intend to monitor.",
          ],
        },
        {
          h: "Acceptable use",
          body: [
            "You agree not to use the Services in any way that violates applicable law or these Terms. In particular, you agree not to:",
          ],
          list: [
            "Reverse engineer, decompile, or attempt to derive the source code of the Services, except to the extent law expressly permits.",
            "Use the Services to send spam, distribute malware, or conduct unauthorised security testing against systems you do not own or have written permission to test.",
            "Interfere with or disrupt the integrity or performance of the Services, or attempt to gain unauthorised access to any part of them.",
            "Use the Services to build a competing product or to benchmark for that purpose without our prior written consent.",
            "Misrepresent your identity or impersonate any person or entity in your use of the Services.",
          ],
        },
        {
          h: "Fees and billing",
          body: [
            "Paid plans are billed monthly or annually in advance, depending on the cycle you select. Fees are non-refundable except where required by law or where we explicitly state otherwise. Annual plans receive the discount displayed at checkout.",
            "We may change pricing on renewal. Add-ons (for example, the Compliance add-on) are billed alongside your base plan and follow the same renewal cadence.",
          ],
        },
        {
          h: "Confidentiality",
          body: [
            "Each party will protect the other's confidential information using at least the same degree of care it uses to protect its own confidential information of similar sensitivity, and not less than reasonable care. Customer telemetry, findings, and audit evidence are confidential information of the customer.",
          ],
        },
        {
          h: "Intellectual property",
          body: [
            "AtmosGuard owns the Services, including the platform, detection logic, control mappings, documentation, and all related intellectual property. You retain all rights in your data and configurations. You grant us only the rights needed to operate the Services for you.",
          ],
        },
        {
          h: "Suspension and termination",
          body: [
            "Either party may terminate for material breach uncured for 30 days after written notice. We may suspend the Services without notice in narrow circumstances, where continued use would expose us, you, or other customers to imminent legal or security risk, and we will restore service as soon as the risk is resolved.",
          ],
        },
        {
          h: "Disclaimers and limits",
          body: [
            "The Services are provided \"as is\" and \"as available\". To the maximum extent permitted by law, AtmosGuard disclaims all implied warranties, including merchantability, fitness for a particular purpose, and non-infringement. AtmosGuard does not guarantee that the Services will detect every risk or threat in your environment.",
            "To the maximum extent permitted by law, neither party will be liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits or revenue, even if advised of the possibility. Each party's aggregate liability arising out of or relating to these Terms will not exceed the fees you paid us in the 12 months preceding the event giving rise to the claim.",
          ],
        },
        {
          h: "Governing law",
          body: [
            "Any dispute arising out of or relating to these Terms will be resolved exclusively in a court of AtmosGuard's choice, and you consent to the exclusive jurisdiction and venue of that court. Where you are an Enterprise customer with a separate signed MSA, the governing law and venue clauses in that MSA control.",
          ],
        },
      ]}
      contactLine={<>Questions about these terms or to request our DPA, MSA, or BAA? Email <a href="mailto:info@atmosguard.io">info@atmosguard.io</a>.</>}
    />
  );
}

/* ---------- App (default home, used by index.html) ---------- */
function App() {
  return <Page active="home"><HomePage /></Page>;
}

/* expose for other script files */
Object.assign(window, {
  App, Page, Ticker,
  PRODUCTS, PRODUCT_LOGOS, ProductLogo,
  Nav, Hero, HeroConsole, Stats, Modules, Module,
  Coverage, Pipeline, Frameworks,
  Docs, CTA, Footer, Tweaks, BrandMark,
  PostureRing, ThreatFeed, ComplianceGrid, Changelog, FeatureCard,
  ComplianceShot, AuditTimeline, EvidenceFlow,
  PricingPage, IntegrationsPage, CustomersPage, DocsPage,
  CompanyPage, CareersPage, ContactPage, BlogIndexPage, BlogPostPage, PageHeader,
  LegalPage, PrivacyPage, TermsPage,
});
